/* eslint-disable max-lines, max-len, id-length */
// View-model and payload normalization helpers for ProjectDetails.
// Kept separate from the React component to keep UI code readable.
import VerticalIcon from '../../../Assets/images/svg/vertical.svg';
import TypeIcon from '../../../Assets/images/svg/type.svg';
import BillingIcon from '../../../Assets/images/svg/billing.svg';
import OfferingIcon from '../../../Assets/images/svg/offering.svg';
import MethodologyIcon from '../../../Assets/images/svg/methodology.svg';
import StaffedIcon from '../../../Assets/images/svg/staffed.svg';
import OverallEffortIcon from '../../../Assets/images/svg/overall_effort.svg';
import EffortIcon from '../../../Assets/images/svg/effort.svg';
import OverallIdleIcon from '../../../Assets/images/svg/overall_idle.svg';
import IdleIcon from '../../../Assets/images/svg/idle.svg';
import JiraIcon from '../../../Assets/images/svg/jira.svg';
import buildRiskRegisterData from './projectDetailsRiskViewmodel';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_DATE_PLACEHOLDER = '9999-01-01';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Pre-compiled regexes (avoids re-creating them on every call)
const RE_ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const RE_NUMERIC_ONLY = /^\d+$/;
const RE_SIGNED_NUMERIC_ONLY = /^-?\d+$/;
const RE_NUMBER_IN_STRING = /-?\d+(\.\d+)?/;
const RE_WHITESPACE = /\s+/g;
const RE_NON_ALPHA = /[^A-Z0-9]/g;
const RE_NON_ALPHA_LOWER = /[^a-z0-9]/g;
const RE_DAY_MON_YEAR = /^(\d{1,2})\s(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec),\s(\d{4})$/i;
const RE_FORMATTED_LIKE_API = /^\d{1,2}-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/i;
const RE_PERCENT_STRIP = /%/g;

// Normalized TM / FP lookup sets
const TM_VARIANTS = new Set(['TM', 'TIMEANDMATERIAL', 'TIMEMATERIAL']);
const FP_VARIANTS = new Set(['FP', 'FPP', 'FIXEDPRICE']);

// Meaningful-display null sentinels
const NULL_SENTINELS = new Set(['-', '--', 'na', 'n/a', 'null', 'undefined']);

// ---------------------------------------------------------------------------
// Primitive helpers
// ---------------------------------------------------------------------------

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const toNumberOrNull = (value) => {
  if (!hasValue(value)) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const toFiniteNumberOrNull = (value) => {
  if (!hasValue(value)) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toPercentOrNull = (value) => {
  if (!hasValue(value)) return null;
  if (typeof value === 'string') {
    return toFiniteNumberOrNull(value.trim().replaceAll(RE_PERCENT_STRIP, ''));
  }
  return toFiniteNumberOrNull(value);
};

const toGMNumberOrNull = (value) => {
  if (typeof value === 'string') {
    const cleaned = value.trim().replaceAll(RE_PERCENT_STRIP, '');
    const upper = cleaned.toUpperCase();
    if (!cleaned || cleaned === '-' || upper === 'NA') return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstValidGMNumber = (...candidates) => (
  candidates.map((v) => toGMNumberOrNull(v)).find((v) => v !== null) ?? 0
);

const firstValidGMNumberOrNull = (...candidates) => (
  candidates.map((v) => toGMNumberOrNull(v)).find((v) => v !== null) ?? null
);

// ---------------------------------------------------------------------------
// Object / path helpers
// ---------------------------------------------------------------------------

const getNestedValue = (obj, path) => {
  if (!obj || typeof obj !== 'object' || !path) return undefined;
  const parts = String(path).split('.')
    .filter(Boolean);
  let cur = obj;
  for (const key of parts) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = cur[key];
  }
  return cur;
};

const firstNonNullish = (...candidates) => candidates.find((v) => v !== null && v !== undefined);

// Some backend payloads use "flat" dotted keys e.g. { "agmValue.gm_percent": 12.34 }
const getDirectOrNestedValue = (obj, path) => firstNonNullish(
  obj && typeof obj === 'object' ? obj[path] : undefined,
  getNestedValue(obj, path),
);

const getObjectFromNestedPath = (obj, path) => {
  const nested = getNestedValue(obj, path);
  return nested && typeof nested === 'object' ? nested : null;
};

// ---------------------------------------------------------------------------
// Display-text helpers
// ---------------------------------------------------------------------------

const toDisplayTextOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text || null;
};

const normalizeEmployeeIdKey = (value) => {
  const text = toDisplayTextOrNull(value);
  return text ? text.toLowerCase() : null;
};

const toMeaningfulDisplayTextOrNull = (value) => {
  const text = toDisplayTextOrNull(value);
  if (!text) return null;
  return NULL_SENTINELS.has(text.toLowerCase()) ? null : text;
};

const sanitizeMetricValueText = (value, fallback = '-') => {
  const text = toMeaningfulDisplayTextOrNull(value);
  if (!text) return fallback;

  const normalized = text.replaceAll(RE_WHITESPACE, ' ').trim();
  const lower = normalized.toLowerCase();
  if (lower === 'nan' || lower === 'nan hrs' || lower === 'nan%') return fallback;

  return normalized;
};

// Returns null for pure-numeric strings (raw DB IDs) so they don't leak into the UI
const toLookupDisplayTextOrNull = (value) => {
  const text = toMeaningfulDisplayTextOrNull(value);
  if (!text) return null;
  return RE_NUMERIC_ONLY.test(text) ? null : text;
};

const isLikelyEmployeeIdentifier = (value) => {
  const text = toDisplayTextOrNull(value);
  if (!text) return false;
  return RE_NUMERIC_ONLY.test(text) || /^[A-Za-z]{1,3}\d+$/.test(text);
};

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

const formatApiDate = (value) => {
  if (!value || value === API_DATE_PLACEHOLDER) return '-';
  const parts = String(value).split('-');
  if (parts.length !== 3) return String(value);
  const [year, month, day] = parts;
  const monthIndex = Number(month) - 1;
  if (!MONTH_NAMES[monthIndex]) return String(value);
  return `${day}-${MONTH_NAMES[monthIndex]}-${year}`;
};

const coerceIsoDatePart = (value) => {
  if (!hasValue(value)) return null;
  const datePart = String(value).split('T')[0].split(' ')[0];
  return RE_ISO_DATE.test(datePart) ? datePart : null;
};

const formatMetricDateOrNull = (value) => {
  const datePart = coerceIsoDatePart(value);
  if (!datePart) return null;
  const formatted = formatApiDate(datePart);
  return formatted === '-' ? null : formatted;
};

const isValidProjectDate = (value) => (
  Boolean(value)
  && value !== API_DATE_PLACEHOLDER
  && !Number.isNaN(new Date(value).getTime())
);

const getDayNameForApiDate = (value) => {
  if (!isValidProjectDate(value)) return '';
  return DAYS[new Date(value).getDay()] || '';
};

const formatDayAndDate = (value) => {
  if (!isValidProjectDate(value)) return '-';
  const day = getDayNameForApiDate(value);
  const date = formatApiDate(value);
  return day ? `${day}, ${date}` : date;
};

const calculateDaysAgo = (dateValue) => {
  if (!isValidProjectDate(dateValue)) return null;
  const diffInMs = Date.now() - new Date(dateValue).getTime();
  return Math.max(0, Math.floor(diffInMs / 86_400_000));
};

const getFiscalYearLabelForDate = (dateValue) => {
  if (!isValidProjectDate(dateValue)) return null;
  const dateObj = new Date(dateValue);
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth() + 1;
  const fyStartYear = month >= 4 ? year : year - 1;
  return `${fyStartYear} - ${String(fyStartYear + 1).slice(-2)}`;
};

const normalizeDisplayMetricDate = (dateText) => {
  const raw = toDisplayTextOrNull(dateText);
  if (!raw) return null;

  const isoDate = coerceIsoDatePart(raw);
  if (isoDate) return formatApiDate(isoDate);

  const normalized = raw.replaceAll(RE_WHITESPACE, ' ');
  const dayMonYearMatch = RE_DAY_MON_YEAR.exec(normalized);
  if (dayMonYearMatch) {
    const day = String(Number(dayMonYearMatch[1])).padStart(2, '0');
    return `${day}-${dayMonYearMatch[2]}-${dayMonYearMatch[3]}`;
  }

  if (RE_FORMATTED_LIKE_API.test(normalized)) {
    const [d, m, y] = normalized.split('-');
    return `${String(Number(d)).padStart(2, '0')}-${m}-${y}`;
  }

  return raw;
};

const normalizeDisplayDateValue = (value, fallback = '-') => {
  const normalized = normalizeDisplayMetricDate(value);
  return normalized || fallback;
};

const splitMetricDisplayText = (value) => {
  const text = toMeaningfulDisplayTextOrNull(value);
  if (!text) return { date: null, value: null };

  const colonIdx = text.indexOf(':');
  if (colonIdx === -1) return { date: null, value: sanitizeMetricValueText(text, null) };

  const left = text.slice(0, colonIdx);
  const right = sanitizeMetricValueText(text.slice(colonIdx + 1), null);
  return {
    date: normalizeDisplayMetricDate(left),
    value: right || '-',
  };
};

const normalizeProjectTypeVariant = (value) => {
  const normalized = String(value || '')
    .trim()
    .toUpperCase()
    .replaceAll(RE_NON_ALPHA, '');

  if (!normalized) return '';
  if (TM_VARIANTS.has(normalized)) return 'tm';
  if (FP_VARIANTS.has(normalized)) return 'fp';
  return '';
};

const parseDisplayMetricDate = (value) => {
  const normalized = normalizeDisplayMetricDate(value);
  if (!normalized) return Number.NEGATIVE_INFINITY;

  const formattedMatch = /^(\d{2})-([A-Za-z]{3})-(\d{4})$/.exec(normalized);
  if (!formattedMatch) return Number.NEGATIVE_INFINITY;

  const [, day, monthText, year] = formattedMatch;
  const monthIndex = MONTH_NAMES.findIndex((month) => month.toLowerCase() === monthText.toLowerCase());
  if (monthIndex === -1) return Number.NEGATIVE_INFINITY;

  return new Date(Number(year), monthIndex, Number(day)).getTime();
};

// ---------------------------------------------------------------------------
// Project-type helpers
// ---------------------------------------------------------------------------

const normalizeProjectTypeValue = (value) => {
  const normalized = String(value || '').trim()
    .toUpperCase()
    .replaceAll(RE_NON_ALPHA, '');
  if (!normalized) return '';
  if (TM_VARIANTS.has(normalized)) return 'TM';
  if (FP_VARIANTS.has(normalized)) return 'FP';
  return String(value || '').trim();
};

const resolveProjectTypeValue = (...candidates) => {
  for (const candidate of candidates) {
    const normalizedValue = normalizeProjectTypeValue(candidate);
    if (normalizedValue === 'TM' || normalizedValue === 'FP') {
      return normalizedValue;
    }
  }

  const numericTypeId = candidates
    .map((candidate) => toNumberOrNull(candidate))
    .find((candidate) => candidate !== null);

  if (numericTypeId === 2) return 'TM';
  if (numericTypeId === 1) return 'FP';

  return normalizeProjectTypeValue(firstNonNullish(...candidates) || '');
};

const isTimeAndMaterialProjectType = (value) => normalizeProjectTypeValue(value) === 'TM';
const isFixedPriceProjectType = (value) => normalizeProjectTypeValue(value) === 'FP';

// ---------------------------------------------------------------------------
// Array helpers
// ---------------------------------------------------------------------------

const firstArray = (candidates) => {
  const rows = (Array.isArray(candidates) ? candidates : []).find(Array.isArray);
  return Array.isArray(rows) && rows.length > 0 ? rows : null;
};

const pickLatestRowByDateKey = (rows, dateKey) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;
  if (!dateKey) return list[0] ?? null;

  return list
    .map((row) => {
      const datePart = coerceIsoDatePart(row?.[dateKey]);
      return { row, score: datePart ? new Date(datePart).getTime() : Number.NEGATIVE_INFINITY };
    })
    .sort((a, b) => b.score - a.score)[0]?.row ?? null;
};

// ---------------------------------------------------------------------------
// People / person helpers
// ---------------------------------------------------------------------------

const normalizePersonDetails = (person) => {
  if (!person || typeof person !== 'object') return null;
  const fullName = [
    toDisplayTextOrNull(person.first_name),
    toDisplayTextOrNull(person.last_name),
  ].filter(Boolean).join(' ');

  return {
    ...person,
    name: firstNonNullish(
      toDisplayTextOrNull(person.name),
      toDisplayTextOrNull(person.emp_name),
      toDisplayTextOrNull(fullName),
      null,
    ),
    emp_id: firstNonNullish(toDisplayTextOrNull(person.emp_id), null),
    emp_no: firstNonNullish(toDisplayTextOrNull(person.emp_no), null),
    id: toDisplayTextOrNull(person.id),
    email_id: firstNonNullish(toDisplayTextOrNull(person.email_id), toDisplayTextOrNull(person.email), null),
  };
};

const getLookupIdentifier = (person, keyType = null) => {
  if (!person || typeof person !== 'object') return null;

  if (keyType === 'emp_id') {
    return firstNonNullish(
      toDisplayTextOrNull(person.emp_id),
      toDisplayTextOrNull(person.id),
      null,
    );
  }

  if (keyType === 'emp_no') {
    return firstNonNullish(
      toDisplayTextOrNull(person.emp_no),
      toDisplayTextOrNull(person.id),
      null,
    );
  }

  return firstNonNullish(
    toDisplayTextOrNull(person.emp_id),
    toDisplayTextOrNull(person.emp_no),
    toDisplayTextOrNull(person.id),
    null,
  );
};

const enrichPersonDetailsFromDirectory = (person, employeeDirectoryMap, preferredKeyType = null) => {
  const normalized = normalizePersonDetails(person);
  if (!normalized) return null;

  const directoryMatch = firstNonNullish(
    preferredKeyType === 'emp_id' ? getEmployeeFromDirectory(employeeDirectoryMap, getLookupIdentifier(normalized, 'emp_id'), 'emp_id') : null,
    preferredKeyType === 'emp_no' ? getEmployeeFromDirectory(employeeDirectoryMap, getLookupIdentifier(normalized, 'emp_no'), 'emp_no') : null,
    getEmployeeFromDirectory(employeeDirectoryMap, normalized?.emp_id, 'emp_id'),
    getEmployeeFromDirectory(employeeDirectoryMap, normalized?.emp_no, 'emp_no'),
    getEmployeeFromDirectory(employeeDirectoryMap, normalized?.id, preferredKeyType),
    null,
  );

  const resolvedName = (
    directoryMatch?.name && isLikelyEmployeeIdentifier(normalized?.name)
      ? directoryMatch.name
      : firstNonNullish(normalized?.name, directoryMatch?.name, null)
  );

  const mergedPerson = directoryMatch
    ? { ...directoryMatch, ...normalized }
    : { ...normalized };

  return {
    ...mergedPerson,
    name: resolvedName,
    emp_id: firstNonNullish(normalized?.emp_id, directoryMatch?.emp_id, preferredKeyType === 'emp_id' ? normalized?.id : null, null),
    emp_no: firstNonNullish(normalized?.emp_no, directoryMatch?.emp_no, preferredKeyType === 'emp_no' ? normalized?.id : null, null),
    email_id: firstNonNullish(normalized?.email_id, directoryMatch?.email_id, null),
  };
};

const getPersonNameFromValue = (value) => {
  if (value && typeof value === 'object') {
    return normalizePersonDetails(value)?.name || null;
  }
  return toDisplayTextOrNull(value);
};

const parseDisplayNames = (value) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => {
        const text = getPersonNameFromValue(item);
        if (!text) return [];
        return String(text).split(',')
          .map((part) => toDisplayTextOrNull(part))
          .filter(Boolean);
      });
  }
  const text = getPersonNameFromValue(value);
  if (!text) return [];
  return text.split(',').map((item) => toDisplayTextOrNull(item))
    .filter(Boolean);
};

const buildPeopleRows = (displayValue, detailsRows, employeeDirectoryMap = null, preferredKeyType = null) => {
  const names = parseDisplayNames(displayValue);
  const details = (Array.isArray(detailsRows) ? detailsRows : [])
    .map((row) => enrichPersonDetailsFromDirectory(row, employeeDirectoryMap, preferredKeyType))
    .filter(Boolean);

  if (names.length > 0) {
    return names.map((name, i) => {
      const resolvedDetails = firstNonNullish(
        details[i],
        isLikelyEmployeeIdentifier(name)
          ? getEmployeeFromDirectory(employeeDirectoryMap, name, preferredKeyType)
          : null,
        null,
      );

      return {
        name: (
          resolvedDetails?.name && isLikelyEmployeeIdentifier(name)
            ? resolvedDetails.name
            : name
        ),
        details: resolvedDetails || null,
      };
    });
  }

  return details.map((detail) => ({ name: detail?.name || '-', details: detail }));
};

const mergePeopleRows = (...groups) => {
  const seen = new Set();
  return groups.flat().filter(Boolean)
    .filter((person) => {
      const key = [
        toDisplayTextOrNull(person?.details?.emp_id),
        toDisplayTextOrNull(person?.details?.emp_no),
        toDisplayTextOrNull(person?.name),
      ].find(Boolean);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const getEmployeeDirectoryMap = (baseData) => {
  const rows = Array.isArray(baseData?.employeeDirectory) ? baseData.employeeDirectory : [];
  return rows.reduce((acc, row) => {
    const normalized = normalizePersonDetails(row);
    if (!normalized) return acc;

    const empIdKey = toDisplayTextOrNull(normalized.emp_id);
    const empNoKey = toDisplayTextOrNull(normalized.emp_no);

    if (empIdKey) {
      acc.set(`emp_id:${empIdKey}`, normalized);
      acc.set(`emp_id:${normalizeEmployeeIdKey(empIdKey)}`, normalized);
    }
    if (empNoKey) acc.set(`emp_no:${empNoKey}`, normalized);
    return acc;
  }, new Map());
};

const getEmployeeFromDirectory = (employeeDirectoryMap, identifier, keyType) => {
  const text = toDisplayTextOrNull(identifier);
  if (!text || !(employeeDirectoryMap instanceof Map)) return null;
  const normalizedEmpIdKey = normalizeEmployeeIdKey(text);

  if (keyType) {
    return (
      employeeDirectoryMap.get(`${keyType}:${text}`)
      || (keyType === 'emp_id' && normalizedEmpIdKey ? employeeDirectoryMap.get(`emp_id:${normalizedEmpIdKey}`) : null)
      || null
    );
  }

  return (
    employeeDirectoryMap.get(`emp_id:${text}`)
    || (normalizedEmpIdKey ? employeeDirectoryMap.get(`emp_id:${normalizedEmpIdKey}`) : null)
    || employeeDirectoryMap.get(`emp_no:${text}`)
    || null
  );
};

const parseEmployeeIdentifiers = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => toDisplayTextOrNull(item)).filter(Boolean);
  }

  const text = toDisplayTextOrNull(value);
  if (!text) return [];

  return text
    .split(',')
    .map((item) => toDisplayTextOrNull(item))
    .filter(Boolean);
};

const buildPeopleRowsFromEmployeeRefs = (ids, employeeDirectoryMap, preferredKeyType = null, isSenior = false) => {
  const identifiers = parseEmployeeIdentifiers(ids);
  const seen = new Set();

  return identifiers
    .map((id) => {
      const details = firstNonNullish(
        preferredKeyType ? getEmployeeFromDirectory(employeeDirectoryMap, id, preferredKeyType) : null,
        getEmployeeFromDirectory(employeeDirectoryMap, id, preferredKeyType === 'emp_no' ? 'emp_id' : 'emp_no'),
        getEmployeeFromDirectory(employeeDirectoryMap, id, null),
        null,
      );

      if (!details?.name) return null;

      const uniqueKey = firstNonNullish(details.emp_id, details.emp_no, details.name, id);
      if (seen.has(uniqueKey)) return null;
      seen.add(uniqueKey);

      return {
        name: details.name,
        details,
        isSenior: Boolean(isSenior),
      };
    })
    .filter(Boolean);
};

const buildManagerRows = (projectRecord, baseData) => {
  const employeeDirectoryMap = getEmployeeDirectoryMap(baseData);
  const managerFieldNames = ['manager_id', ...Array.from({ length: 14 }, (_, index) => `manager_${index + 2}`)];
  const managerDetailsRows = firstNonNullish(
    projectRecord?.manager_details,
    getNestedValue(baseData, 'data.manager_details'),
    [],
  );
  const normalizedManagerDetails = (Array.isArray(managerDetailsRows) ? managerDetailsRows : [])
    .map((row) => enrichPersonDetailsFromDirectory(row, employeeDirectoryMap, 'emp_id'))
    .filter((row) => toDisplayTextOrNull(row?.name));

  if (normalizedManagerDetails.length > 0) {
    return mergePeopleRows(normalizedManagerDetails.map((detail) => ({ name: detail.name, details: detail })));
  }

  const rows = [];
  for (const fieldName of managerFieldNames) {
    const managerNameField = `${fieldName}_name`;
    const managerId = firstNonNullish(
      toDisplayTextOrNull(projectRecord?.[fieldName]),
      toDisplayTextOrNull(getNestedValue(baseData, `data.${fieldName}`)),
      null,
    );
    const normalizedManager = getEmployeeFromDirectory(employeeDirectoryMap, managerId, 'emp_id');
    const payloadManagerName = firstNonNullish(
      toDisplayTextOrNull(projectRecord?.[managerNameField]),
      toDisplayTextOrNull(getNestedValue(baseData, `data.${managerNameField}`)),
      null,
    );
    const name = firstNonNullish(
      normalizedManager?.name,
      payloadManagerName,
      null,
    );
    if (!name) continue;
    const resolvedName = (
      normalizedManager?.name && isLikelyEmployeeIdentifier(payloadManagerName)
        ? normalizedManager.name
        : name
    );
    rows.push({
      name: resolvedName,
      details: normalizedManager || normalizePersonDetails({ name: resolvedName, emp_id: managerId, id: managerId }),
    });
  }
  return mergePeopleRows(rows);
};

// ---------------------------------------------------------------------------
// GM / percent helpers
// ---------------------------------------------------------------------------

const getPercentFromRow = (row) => toPercentOrNull(firstNonNullish(
  row?.gm_percent, row?.gmPercent, row?.gm_percentage, row?.gmPercentage, row?.gm,
));

const getDateFromRow = (row, ...keys) => {
  if (!row || typeof row !== 'object') return null;
  const value = keys.map((k) => row?.[k]).find((v) => hasValue(v));
  return formatMetricDateOrNull(value);
};

// ---------------------------------------------------------------------------
// Progress helpers
// ---------------------------------------------------------------------------

const formatProgressPercent = (value) => {
  const num = toFiniteNumberOrNull(value);
  return num === null ? null : Number(num.toFixed(2));
};

const isDbProgressRow = (row) => Boolean(
  row && typeof row === 'object' && ('progress_id' in row || 'progress_percent' in row || 'project_id' in row),
);

const getRawProgressRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  return firstArray([
    payload.project_progress, payload.projectProgress, payload.projectProgressRows,
    payload.project_progress_rows, payload.progress, payload.progressRows,
    payload.progress_rows, payload.progressHistory, payload.progress_history,
  ]);
};

const buildProgressViewModelFromDbRows = (rows) => {
  const activeRows = (Array.isArray(rows) ? rows : [])
    .filter((row) => row && typeof row === 'object' && !row.is_delete);

  if (!activeRows.length) return [];

  return [...activeRows]
    .sort((a, b) => {
      const at = isValidProjectDate(a.date) ? new Date(a.date).getTime() : 0;
      const bt = isValidProjectDate(b.date) ? new Date(b.date).getTime() : 0;
      return bt - at;
    })
    .map((row) => {
      const progressRowId = row.progress_id ?? row.id ?? `${row.project_id || 'project'}_${row.date || 'progress'}`;
      return ({
        id: progressRowId,
        row_id: progressRowId,
        project_id: row.project_id ?? row.projectId ?? null,
        date: formatApiDate(row.date),
        day: getDayNameForApiDate(row.date),
        progress: formatProgressPercent(row.progress_percent) ?? 0,
        remarks: row.remarks ?? '',
      });
    });
};

// ---------------------------------------------------------------------------
// Change requests / efforts helpers
// ---------------------------------------------------------------------------

const getEffortEstimationRowsFromCr = (cr) => {
  if (!cr || typeof cr !== 'object') return [];
  const rows = [cr.effortEstimations, cr.effort_estimations, cr.effortEstimation, cr.effort_estimation,
    cr.effortEstimationRows, cr.effort_estimation_rows].find(Array.isArray);
  return Array.isArray(rows) ? rows : [];
};

const getRawInitialEffortRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload.efforts || payload.effortDetails || payload.effort_details || null;
  return firstArray([
    payload.initialEffortEstimations,
    payload.initial_effort_estimations,
    payload.effortEstimationList,
    payload.effort_estimation_list,
    payload.effortEstimations,
    payload.effort_estimations,
    nested?.initialEffortEstimations,
    nested?.initial_effort_estimations,
    nested?.effortEstimationList,
    nested?.effort_estimation_list,
  ]);
};

const getRawChangeRequestsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload.efforts || payload.effortDetails || payload.effort_details || null;
  return firstArray([
    payload.changeRequests, payload.change_requests, payload.projectChangeRequests,
    payload.project_change_requests, payload.crs,
    nested?.changeRequests, nested?.change_requests,
  ]);
};

const EFFORT_ESTIMATION_TOTAL_FIELDS = [
  'efforts_hours',
  'estimated_efforts',
  'actual_efforts',
  'shadow',
  'junior',
  'regular',
  'niche',
  'premium',
];

const toEffortEstimationNumber = (value) => toFiniteNumberOrNull(value) ?? 0;
const compareEffortEstimationRows = (firstRow, secondRow) => {
  const categoryCompare = String(firstRow.resource_category || '').localeCompare(
    String(secondRow.resource_category || ''),
    undefined,
    { sensitivity: 'base' },
  );
  if (categoryCompare !== 0) return categoryCompare;

  const typeCompare = String(firstRow.resource_type || '').localeCompare(
    String(secondRow.resource_type || ''),
    undefined,
    { sensitivity: 'base' },
  );
  if (typeCompare !== 0) return typeCompare;

  return String(firstRow.effort_id || '').localeCompare(String(secondRow.effort_id || ''));
};

const buildLegacyEffortRow = (row, index, resourceTypeLookup = null, resourceCategoryLookup = null) => {
  const effortId = firstNonNullish(row.effort_id, row.effortId, row.id, `effort_${index + 1}`);
  const typeId = firstNonNullish(row.type_id, row.typeId, null);
  const categoryId = firstNonNullish(row.category_id, row.categoryId, null);
  const crId = firstNonNullish(row.cr_id, row.crId, null);
  const resourceType = firstNonNullish(
    row.resource_type,
    row.resourceType,
    typeId !== null && resourceTypeLookup ? resourceTypeLookup.get(String(typeId)) : null,
    '',
  );
  const resourceCategory = firstNonNullish(
    row.resource_category,
    row.resourceCategory,
    categoryId !== null && resourceCategoryLookup ? resourceCategoryLookup.get(String(categoryId)) : null,
    '',
  );
  const effortHours = toEffortEstimationNumber(row.efforts_hours ?? row.effortsHours);
  const estimatedEfforts = toEffortEstimationNumber(row.estimated_efforts ?? row.estimatedEfforts);
  const displayEffortHours = effortHours > 0
    ? effortHours
    : (
      estimatedEfforts
      || toEffortEstimationNumber(row.efforts ?? row.efforts_hour ?? row.effortsHour)
      || 0
    );

  return {
    id: effortId,
    row_id: effortId,
    _entity_type: 'effort_estimation',
    effort_id: effortId,
    proposal_id: firstNonNullish(row.proposal_id, row.proposalId, null),
    category_id: categoryId,
    type_id: typeId,
    resource_category: String(resourceCategory || ''),
    resource_type: String(resourceType || ''),
    details: String(firstNonNullish(row.details, '') || ''),
    efforts_hours: effortHours,
    efforts_hours_display: displayEffortHours,
    estimated_efforts: estimatedEfforts,
    actual_efforts: toEffortEstimationNumber(row.actual_efforts ?? row.actualEfforts),
    shadow: toEffortEstimationNumber(row.shadow),
    junior: toEffortEstimationNumber(row.junior),
    regular: toEffortEstimationNumber(row.regular),
    niche: toEffortEstimationNumber(row.niche),
    premium: toEffortEstimationNumber(row.premium),
    remarks: String(firstNonNullish(row.remarks, '') || ''),
    cr_id: crId,
    account_name: row.account_name ?? null,
    cr_number: row.cr_number ?? null,
    cr_status_id: row.cr_status_id ?? null,
    cr_status_value: row.cr_status_value ?? null,
    efforts_hour: row.efforts_hour ?? null,
    efforts_investment: row.efforts_investment ?? null,
    customer_approved_status: row.customer_approved_status ?? null,
    customer_status: row.customer_status ?? null,
  };
};

const dedupeInitialEffortRows = (initialEffortRows) => Array.from(
  (Array.isArray(initialEffortRows) ? initialEffortRows : [])
    .filter((row) => row && typeof row === 'object')
    .reduce((lookup, row, index) => {
      const effortId = firstNonNullish(row.effort_id, row.effortId, row.id, `effort_${index + 1}`);
      if (!lookup.has(String(effortId))) {
        lookup.set(String(effortId), row);
      }
      return lookup;
    }, new Map())
    .values(),
);

const formatCrInvestmentValue = (value) => {
  const normalizedValue = String(value || '')
    .trim()
    .toLowerCase();
  if (['y', 'yes', '1', 'true'].includes(normalizedValue)) return 'Yes';
  if (['n', 'no', '0', 'false'].includes(normalizedValue)) return 'No';
  return String(value || '').trim() || '-';
};

const formatCrCustomerApprovalValue = (value) => {
  const normalizedValue = String(value || '')
    .trim()
    .toLowerCase();
  if (!normalizedValue) return 'Not Applicable';
  if (['1', 'approved', 'yes', 'y', 'true'].includes(normalizedValue)) return 'Approved';
  if (['0', 'pending', 'no', 'n', 'false'].includes(normalizedValue)) return 'Pending';
  if (['2', 'rejected', 'reject', 'declined'].includes(normalizedValue)) return 'Rejected';
  if (['3', 'sent for approval'].includes(normalizedValue)) return 'Sent for Approval';
  if (['4', 'yet to send for approval'].includes(normalizedValue)) return 'Yet to send for Approval';
  if (['5', 'not applicable', 'na', 'n/a'].includes(normalizedValue)) return 'Not Applicable';
  return String(value || '').trim() || 'Not Applicable';
};

const formatCrStatusValue = (statusValue, statusId) => {
  const directStatus = String(statusValue || '').trim();
  if (directStatus) return directStatus;

  const normalizedStatusId = toFiniteNumberOrNull(statusId);
  if (normalizedStatusId === 1) return 'In Progress';
  if (normalizedStatusId === 2) return 'Approved';
  if (normalizedStatusId === 3) return 'Cancelled';
  return '-';
};

const getParsedCrId = (row) => {
  const raw = firstNonNullish(row?.cr_id, row?.crId, null);
  if (raw === null || raw === undefined || raw === '') return null;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
};

const buildLegacyEffortGroups = ({ rows, changeRequests, resourceTypeLookup, resourceCategoryLookup }) => {
  const legacyInitialRows = rows.filter((row) => getParsedCrId(row) === null);
  const baseAddedRows = rows.filter((row) => getParsedCrId(row) === 0);
  const crEffortRows = rows.filter((row) => {
    const crId = getParsedCrId(row);
    return crId !== null && crId > 0;
  });

  const crEffortRowsMap = new Map();
  crEffortRows.forEach((row) => {
    const crId = getParsedCrId(row);
    if (crId > 0) {
      const existing = crEffortRowsMap.get(crId) || [];
      existing.push(row);
      crEffortRowsMap.set(crId, existing);
    }
  });

  const initialGroup = legacyInitialRows.length ? {
    id: 'initial_effort_estimation',
    row_id: 'initial_effort_estimation',
    displayOrder: 1,
    crNumber: '-',
    crName: 'Initial Efforts Estimation',
    effortInvestment: '-',
    crStatus: '-',
    customerApprovalStatus: '-',
    totalHours: legacyInitialRows.reduce((sum, row) => sum + Number(row?.efforts_hours_display || row?.efforts_hours || 0), 0),
    children: legacyInitialRows,
    isInitialGroup: true,
  } : null;

  const baseAddedGroups = baseAddedRows.length ? [{
    id: 'initial_effort_added_group',
    row_id: 'initial_effort_added_group',
    displayOrder: 3,
    crNumber: '-',
    crName: 'Initial Efforts Estimation',
    effortInvestment: '-',
    crStatus: '-',
    customerApprovalStatus: '-',
    totalHours: baseAddedRows.reduce((sum, row) => sum + Number(row?.efforts_hours_display || row?.efforts_hours || 0), 0),
    children: baseAddedRows.map((row) => ({ ...row, isBaseAddedInitialRow: true })),
    isInitialGroup: false,
    isBaseAddedGroup: true,
  }] : [];

  const processedCrIds = new Set();

  const crGroupsFromRequests = (Array.isArray(changeRequests) ? changeRequests : [])
    .filter((cr) => {
      if (!cr || typeof cr !== 'object') return false;

      // Check if CR has nested effort estimations OR linked flat rows in crEffortRowsMap
      const crId = firstNonNullish(cr.cr_id, cr.crId, cr.change_request_id, cr.changeRequestId, null);
      const parsedCrId = Number(crId);

      const nestedEstimations = getEffortEstimationRowsFromCr(cr);
      const flatEstimations = parsedCrId > 0 ? (crEffortRowsMap.get(parsedCrId) || []) : [];

      // ONLY process CRs that have at least 1 effort estimation line item
      return (nestedEstimations.length + flatEstimations.length) > 0;
    })
    .map((cr, crIndex) => {
      const crId = firstNonNullish(cr.cr_id, cr.crId, cr.change_request_id, cr.changeRequestId, null);
      const parsedCrId = Number(crId);
      if (parsedCrId > 0) processedCrIds.add(parsedCrId);

      const rawChildRows = [
        ...getEffortEstimationRowsFromCr(cr),
        ...(parsedCrId > 0 ? (crEffortRowsMap.get(parsedCrId) || []) : []),
      ];

      const childRows = rawChildRows
        .filter((row) => row && typeof row === 'object')
        .map((row, index) => buildLegacyEffortRow(
          { ...row, cr_id: firstNonNullish(row.cr_id, row.crId, crId) },
          index,
          resourceTypeLookup,
          resourceCategoryLookup,
        ))
        .sort(compareEffortEstimationRows);

      const crStatusId = toFiniteNumberOrNull(firstNonNullish(cr.cr_status_id, cr.crStatusId, childRows[0]?.cr_status_id, null));
      const resolvedCrStatus = formatCrStatusValue(
        firstNonNullish(cr.cr_status, cr.crStatus, cr.cr_status_value, cr.crStatusValue, childRows[0]?.cr_status_value, null),
        crStatusId,
      );
      const crHours = childRows.reduce((sum, row) => sum + Number(row?.efforts_hours_display || row?.efforts_hours || 0), 0)
        || toEffortEstimationNumber(firstNonNullish(cr.efforts_hour, cr.effort_hour, cr.effortsHour, cr.effortHour, 0));

      return {
        id: crId ?? `cr_${crIndex + 1}`,
        row_id: crId ?? `cr_${crIndex + 1}`,
        displayOrder: 2,
        crNumber: String(firstNonNullish(cr.cr_number, cr.crNumber, cr.cr_no, cr.crNo, childRows[0]?.cr_number, '-')),
        crName: String(firstNonNullish(cr.account_name, cr.accountName, cr.cr_name, cr.crName, childRows[0]?.account_name, 'Initial Efforts Estimation')),
        effortInvestment: formatCrInvestmentValue(firstNonNullish(cr.efforts_investment, cr.effortsInvestment, childRows[0]?.efforts_investment, null)),
        crStatus: resolvedCrStatus,
        customerApprovalStatus: formatCrCustomerApprovalValue(
          firstNonNullish(
            cr.customer_approved_status_name,
            cr.customerApprovedStatusName,
            cr.customer_approved_status_label,
            cr.customerApprovedStatusLabel,
            cr.customer_approved_status,
            cr.customerApprovedStatus,
            cr.customer_status,
            cr.customerStatus,
            childRows[0]?.customer_status,
            childRows[0]?.customer_approved_status,
            null,
          ),
        ),
        totalHours: crHours,
        children: childRows,
        isInitialGroup: false,
        // Hide if no children exist OR if cancelled (matches PHP logic)
        _hiddenByLegacyCondition: (
          childRows.length === 0
          || Number(crStatusId) === 3
          || String(resolvedCrStatus).trim()
            .toLowerCase() === 'cancelled'
        ),
      };
    });

  const autoCrGroups = [];
  crEffortRowsMap.forEach((flatChildRows, crId) => {
    if (processedCrIds.has(crId)) return;

    const childRows = flatChildRows
      .map((row, index) => buildLegacyEffortRow(row, index, resourceTypeLookup, resourceCategoryLookup))
      .sort(compareEffortEstimationRows);

    const firstRow = flatChildRows[0] || {};
    const crStatusId = toFiniteNumberOrNull(firstRow.cr_status_id);
    const resolvedCrStatus = formatCrStatusValue(
      firstRow.cr_status_value || firstRow.cr_status || firstRow.crStatus,
      crStatusId,
    );
    const crHours = childRows.reduce((sum, row) => sum + Number(row?.efforts_hours_display || row?.efforts_hours || 0), 0)
      || toEffortEstimationNumber(firstRow.efforts_hour || firstRow.efforts_investment || 0);

    autoCrGroups.push({
      id: crId,
      row_id: crId,
      displayOrder: 2,
      crNumber: String(firstNonNullish(firstRow.cr_number, firstRow.crNumber, '-')),
      crName: String(firstNonNullish(firstRow.account_name, firstRow.accountName, 'Initial Efforts Estimation')),
      effortInvestment: formatCrInvestmentValue(firstNonNullish(firstRow.efforts_investment, firstRow.effortsInvestment, null)),
      crStatus: resolvedCrStatus,
      customerApprovalStatus: formatCrCustomerApprovalValue(
        firstNonNullish(firstRow.customer_status, firstRow.customerStatus, firstRow.customer_approved_status, null),
      ),
      totalHours: crHours,
      children: childRows,
      isInitialGroup: false,
      // Hide if no children exist OR if cancelled (matches PHP logic)
      _hiddenByLegacyCondition: (
        childRows.length === 0
        || Number(crStatusId) === 3
        || String(resolvedCrStatus).trim()
          .toLowerCase() === 'cancelled'
      ),
    });
  });

  const crGroups = [...crGroupsFromRequests, ...autoCrGroups]
    .filter((group) => !group._hiddenByLegacyCondition);

  return { initialGroup, baseAddedGroups, crGroups };
};

const buildLegacyEffortEstimationViewModel = ({
  initialEffortRows,
  changeRequests,
  resourceTypeLookup = null,
  resourceCategoryLookup = null,
}) => {
  const rows = dedupeInitialEffortRows(initialEffortRows)
    .map((row, index) => buildLegacyEffortRow(row, index, resourceTypeLookup, resourceCategoryLookup))
    .sort(compareEffortEstimationRows);

  const totals = EFFORT_ESTIMATION_TOTAL_FIELDS.reduce((acc, field) => {
    acc[field] = rows.reduce((sum, row) => sum + toEffortEstimationNumber(row[field]), 0);
    return acc;
  }, {});

  const { initialGroup, baseAddedGroups, crGroups } = buildLegacyEffortGroups({
    rows,
    changeRequests,
    resourceTypeLookup,
    resourceCategoryLookup,
  });

  return {
    rows: [
      ...rows,
      ...crGroups.flatMap((group) => (Array.isArray(group.children) ? group.children : [])),
    ],
    groups: [
      ...(initialGroup ? [initialGroup] : []),
      ...crGroups,
      ...baseAddedGroups,
    ],
    totals,
    total: (
      (initialGroup?.totalHours || 0)
      + crGroups.reduce((sum, group) => sum + Number(group?.totalHours || 0), 0)
      + baseAddedGroups.reduce((sum, group) => sum + Number(group?.totalHours || 0), 0)
    ),
    resourceTypeOptions: [],
    resourceCategoryOptions: [],
  };
};

// ---------------------------------------------------------------------------
// Resource helpers
// ---------------------------------------------------------------------------

const getRawResourceAllocRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload.resources ?? payload.resourceData ?? payload.resource_details ?? payload.resourceDetails ?? null;
  return firstArray([
    payload.resource_allocation, payload.resourceAllocation, payload.resourceAllocations,
    payload.resourceAllocationRows, payload.resource_allocation_rows,
    payload.resourceAllocationDetails, payload.resource_allocation_details,
    nested?.resource_allocation, nested?.resourceAllocation, nested?.resourceAllocations,
    nested?.resourceAllocationRows,
  ]);
};

const getRawResourceTypeRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  return firstArray([
    payload.resource_type, payload.resourceType, payload.resourceTypes,
    payload.resource_type_rows, payload.resourceTypeRows, payload.type_master, payload.typeMaster,
  ]);
};

const buildResourceTypeMeta = (rows) => {
  const resourceTypeLookup = new Map();
  const resourceTypeOrderLookup = new Map();
  const resourceTypeOptions = [];
  const resourceCategoryOptions = [];
  const categorySeen = new Set();

  (Array.isArray(rows) ? rows : []).filter((row) => row && typeof row === 'object').forEach((row, index) => {
    const typeId = row.type_id ?? row.typeId;
    const typeName = row.resource_type ?? row.resourceType;
    if (typeId !== null && typeId !== undefined && typeName) {
      resourceTypeLookup.set(String(typeId), String(typeName));
      resourceTypeOrderLookup.set(String(typeId), index);
      resourceTypeOptions.push({ value: typeId, label: String(typeName) });
    }
    const categoryId = firstNonNullish(
      row.category_id, row.categoryId, row.resource_category_id, row.resourceCategoryId, row.resource_category, row.resourceCategory,
    );
    const categoryName = firstNonNullish(
      row.resource_category, row.resourceCategory, row.category_name, row.categoryName, categoryId,
    );
    if (categoryId === null || categoryId === undefined || !categoryName) return;
    const key = String(categoryId);
    if (categorySeen.has(key)) return;
    categorySeen.add(key);
    resourceCategoryOptions.push({ value: categoryId, label: String(categoryName) });
  });

  return {
    resourceTypeLookup,
    resourceTypeOrderLookup,
    resourceTypeOptions,
    resourceCategoryOptions,
  };
};

const getRawResourceCategoryRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  return firstArray([
    payload.resource_category,
    payload.resourceCategory,
    payload.resourceCategories,
    payload.resource_category_rows,
    payload.resourceCategoryRows,
    payload.category_master,
    payload.categoryMaster,
  ]);
};

const buildResourceCategoryLookup = (rows) => (
  (Array.isArray(rows) ? rows : []).reduce((lookup, row) => {
    const categoryId = firstNonNullish(
      row?.category_id,
      row?.categoryId,
      row?.resource_category_id,
      row?.resourceCategoryId,
      null,
    );
    const categoryName = firstNonNullish(
      row?.resource_category,
      row?.resourceCategory,
      row?.category_name,
      row?.categoryName,
      null,
    );

    if (categoryId === null || categoryId === undefined || !categoryName) {
      return lookup;
    }

    lookup.set(String(categoryId), String(categoryName));
    return lookup;
  }, new Map())
);

const buildResourceCategoryOptions = (rows) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => {
      const isListValue = firstNonNullish(row?.is_list, row?.isList, null);
      if (isListValue === null || isListValue === undefined || isListValue === '') {
        return true;
      }

      return [1, '1', true, 'true'].includes(isListValue);
    })
    .map((row) => {
      const categoryId = firstNonNullish(
        row?.category_id,
        row?.categoryId,
        row?.resource_category_id,
        row?.resourceCategoryId,
        null,
      );
      const categoryName = firstNonNullish(
        row?.resource_category,
        row?.resourceCategory,
        row?.category_name,
        row?.categoryName,
        null,
      );

      if (categoryId === null || categoryId === undefined || !categoryName) {
        return null;
      }

      return { value: categoryId, label: String(categoryName) };
    })
    .filter(Boolean)
);

const getRawProjectUnitRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload?.data && typeof payload.data === 'object' ? payload.data : null;
  return firstArray([
    payload.projectUnitList,
    payload.projectUnits,
    payload.project_units,
    payload.project_unit,
    nested?.projectUnitList,
    nested?.projectUnits,
    nested?.project_units,
    nested?.project_unit,
  ]);
};

const formatDisplayNumber = (value) => {
  const numberValue = toFiniteNumberOrNull(value);
  if (numberValue === null) return null;
  return String(numberValue)
    .replace(/(\.\d*?[1-9])0+$/u, '$1')
    .replace(/\.0+$/u, '');
};

const buildProjectUnitsData = (baseData, resolvedProjectType) => {
  if (!isTimeAndMaterialProjectType(resolvedProjectType)) {
    return {
      isApplicable: false,
      rows: [],
      resourceCategoryOptions: [],
    };
  }

  const projectUnitRows = getRawProjectUnitRowsFromPayload(baseData);
  const { resourceTypeLookup, resourceTypeOptions } = buildResourceTypeMeta(
    getRawResourceTypeRowsFromPayload(baseData),
  );

  if (!Array.isArray(projectUnitRows) || !projectUnitRows.length) {
    return {
      isApplicable: true,
      rows: [],
      resourceCategoryOptions: resourceTypeOptions,
    };
  }

  return {
    isApplicable: true,
    resourceCategoryOptions: resourceTypeOptions,
    rows: projectUnitRows.map((row, index) => {
      const quantityText = formatDisplayNumber(row?.quantity) || '-';
      const skillText = toMeaningfulDisplayTextOrNull(row?.skills) || '-';
      const unitPriceText = formatDisplayNumber(firstNonNullish(row?.unitPrice, row?.unit_price, null)) || '-';
      const remarksText = toMeaningfulDisplayTextOrNull(row?.remarks) || '-';
      const unitId = firstNonNullish(row?.unitId, row?.unit_id, index + 1);
      const resourceCategoryKey = firstNonNullish(row?.resourceCategory, row?.resource_category, null);
      const resourceCategoryText = resourceCategoryKey !== null && resourceCategoryKey !== undefined
        ? resourceTypeLookup.get(String(resourceCategoryKey)) || String(resourceCategoryKey)
        : '-';
      const startDateText = formatMetricDateOrNull(firstNonNullish(row?.startDate, row?.start_date, null)) || '-';
      const endDateText = formatMetricDateOrNull(firstNonNullish(row?.endDate, row?.end_date, null)) || '-';

      return {
        id: String(unitId),
        projectId: firstNonNullish(row?.projectId, row?.project_id, null),
        resourceCategoryId: resourceCategoryKey,
        quantity: quantityText,
        skills: skillText,
        resourceCategory: resourceCategoryText,
        startDate: startDateText,
        endDate: endDateText,
        unitPrice: unitPriceText,
        unitPriceValue: Number(firstNonNullish(row?.unitPrice, row?.unit_price, 0)) || 0,
        remarks: remarksText,
      };
    }),
  };
};

const clamp0To100 = (value) => {
  const num = toFiniteNumberOrNull(value);
  if (num === null) return 0;
  return Math.max(0, Math.min(100, num));
};

const inferAllocStatus = (row) => {
  const endDate = row?.end_date;
  if (isValidProjectDate(endDate) && new Date(endDate).getTime() < Date.now() && row?.is_shadow) return 'Moved Out';
  if (row?.is_shadow) return 'Shadow';
  if (row?.jira_access_removed) return 'Moved Out';
  if (isValidProjectDate(endDate) && new Date(endDate).getTime() < Date.now()) return 'Moved Out';
  return 'Active';
};

const inferAllocPercent = (row) => {
  const billability = toFiniteNumberOrNull(row?.billability);
  if (billability !== null && billability >= 2 && billability <= 100) return clamp0To100(billability);
  return clamp0To100(row?.efforts);
};

const resolveResourceAllocationName = (row, employeeDirectoryMap) => {
  const rawName = toMeaningfulDisplayTextOrNull(
    firstNonNullish(
      row?.employee_name,
      row?.employeeName,
      row?.full_name,
      row?.fullName,
      row?.resource_name,
      row?.resourceName,
      row?.name,
      null,
    ),
  );
  const employeeDetails = getEmployeeFromDirectory(
    employeeDirectoryMap,
    firstNonNullish(row?.emp_id, row?.empId, row?.employee_id, row?.employeeId, null),
    'emp_id',
  );

  if (
    rawName
    && !RE_SIGNED_NUMERIC_ONLY.test(rawName)
    && !isLikelyEmployeeIdentifier(rawName)
  ) {
    return rawName;
  }

  return firstNonNullish(employeeDetails?.name, rawName, '-');
};

const resolveResourceAllocationRole = (row, resourceTypeLookup) => {
  const typeId = firstNonNullish(row?.resource_type, row?.resourceType, null);
  const resolvedType = (
    typeId !== null
    && typeId !== undefined
    && resourceTypeLookup instanceof Map
      ? resourceTypeLookup.get(String(typeId))
      : null
  );

  return firstNonNullish(
    resolvedType,
    toMeaningfulDisplayTextOrNull(typeId),
    '-',
  );
};

const getResourceAllocationSortValue = (row) => {
  const allocationDate = firstNonNullish(row?.allocation_date, row?.allocationDate, null);
  if (allocationDate && !Number.isNaN(new Date(allocationDate).getTime())) {
    return new Date(allocationDate).getTime();
  }

  const startDate = firstNonNullish(row?.start_date, row?.startDate, null);
  if (startDate && !Number.isNaN(new Date(startDate).getTime())) {
    return new Date(startDate).getTime();
  }

  return Number(firstNonNullish(row?.allocation_id, row?.allocationId, row?.id, 0)) || 0;
};

const getResourceStatusSortPriority = (status = '') => {
  const normalizedStatus = String(status || '').trim()
    .toLowerCase();
  if (normalizedStatus === 'active' || normalizedStatus === 'shadow') return 0;
  if (normalizedStatus === 'moved out') return 2;
  return 1;
};

const getResourceAllocationDefaultSortValue = (allocation) => {
  const dateValue = firstNonNullish(allocation?.startDateValue, allocation?.endDateValue, null);
  if (dateValue && !Number.isNaN(new Date(dateValue).getTime())) {
    return new Date(dateValue).getTime();
  }
  return Number(allocation?.allocationSortValue || 0);
};

const buildResourceAllocVm = (
  rows,
  { employeeDirectoryMap = null, resourceTypeLookup = null, resourceTypeOrderLookup = null, tbhRows = [] } = {},
) => {
  const items = (Array.isArray(rows) ? rows : []).filter((row) => row && typeof row === 'object');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const allocations = items.map((row) => {
    const status = inferAllocStatus(row);
    return {
      id: row.allocation_id
        ?? row.allocationId
        ?? row.id
        ?? `${row.project_id || row.projectId || 'project'}_${row.emp_id || row.empId || row.resource_name || row.resourceName || 'alloc'}`,
      name: resolveResourceAllocationName(row, employeeDirectoryMap),
      employeeId: row.emp_id ?? row.empId ?? row.employee_id ?? row.employeeId ?? '',
      role: resolveResourceAllocationRole(row, resourceTypeLookup),
      status,
      skill: row.skills ?? row.skill ?? '',
      allocation: inferAllocPercent(row),
      allocationId: row.allocation_id ?? row.allocationId ?? row.id ?? null,
      projectId: row.project_id ?? row.projectId ?? null,
      resourceTypeId: row.resource_type ?? row.resourceType ?? null,
      remarks: row.remarks ?? '',
      startDateValue: row.start_date ?? row.startDate ?? null,
      endDateValue: row.end_date ?? row.endDate ?? null,
      startDate: formatDayAndDate(row.start_date ?? row.startDate),
      endDate: formatDayAndDate(row.end_date ?? row.endDate),
      allocationSortValue: getResourceAllocationSortValue(row),
      allocatedDays: Number(firstNonNullish(
        toFiniteNumberOrNull(row?.allocated_days),
        toFiniteNumberOrNull(row?.allocatedDays),
      ) || 0).toFixed(2),
      allocatedHours: Number(firstNonNullish(
        toFiniteNumberOrNull(row?.allocated_hours),
        toFiniteNumberOrNull(row?.allocatedHours),
      ) || 0).toFixed(2),
      scrum: row.scrum ?? (row.scrum_id || row.scrumId ? `scrum${row.scrum_id ?? row.scrumId}` : 'scrum1'),
      efforts: row.efforts ?? row.effort ?? 0,
      isShadow: row.is_shadow ?? row.isShadow ?? 0,
      srNumber: row.sr_number ?? row.srNumber ?? '',
      ctc: row.ctc ?? null,
      unitPrice: row.unit_price ?? row.unitPrice ?? null,
      unitId: row.unit_id ?? row.unitId ?? null,
      rawRow: row,
    };
  });

  allocations.sort((a, b) => {
    const statusPriorityDifference = getResourceStatusSortPriority(a?.status)
      - getResourceStatusSortPriority(b?.status);
    if (statusPriorityDifference !== 0) return statusPriorityDifference;
    return getResourceAllocationDefaultSortValue(b) - getResourceAllocationDefaultSortValue(a);
  });

  const open = allocations.filter((r) => r.status === 'Active').length;
  const closed = allocations.filter((r) => r.status === 'Moved Out').length;
  const days = allocations.reduce((s, r) => s + (toFiniteNumberOrNull(r.allocatedDays) ?? 0), 0);
  const hours = allocations.reduce((s, r) => s + (toFiniteNumberOrNull(r.allocatedHours) ?? 0), 0);

  const roleMap = new Map();
  for (const row of items) {
    const resolvedRole = resolveResourceAllocationRole(row, resourceTypeLookup);
    const role = (resolvedRole && resolvedRole !== '-') ? resolvedRole
      : (String(row.skills || row.skill || '').split(',')
        .map((token) => token.trim())
        .find(Boolean) || '-');
    const entry = roleMap.get(role) || {
      role,
      typeOrder: (
        resourceTypeOrderLookup instanceof Map
        && hasValue(firstNonNullish(row?.resource_type, row?.resourceType, null))
      )
        ? resourceTypeOrderLookup.get(String(firstNonNullish(row?.resource_type, row?.resourceType, null)))
        : Number.MAX_SAFE_INTEGER,
      totalEmployees: new Set(),
      currentEmployees: new Set(),
      allocation: 0,
    };

    const employeeIdentity = firstNonNullish(
      toMeaningfulDisplayTextOrNull(row.emp_id ?? row.empId ?? row.employee_id ?? row.employeeId),
      toMeaningfulDisplayTextOrNull(row.resource_name ?? row.resourceName ?? row.name),
      `${role}_${roleMap.size}`,
    );
    entry.totalEmployees.add(String(employeeIdentity));

    const endDateRaw = firstNonNullish(row.end_date, row.endDate, null);
    const currentCountEligible = (
      isValidProjectDate(endDateRaw)
      && new Date(endDateRaw).getTime() >= today.getTime()
    );
    if (currentCountEligible) {
      entry.currentEmployees.add(String(employeeIdentity));
    }

    const startDateRaw = firstNonNullish(row.start_date, row.startDate, null);
    const allocationActive = (
      isValidProjectDate(startDateRaw)
      && isValidProjectDate(endDateRaw)
      && new Date(startDateRaw).getTime() <= today.getTime()
      && new Date(endDateRaw).getTime() >= today.getTime()
    );
    if (allocationActive) {
      entry.allocation += Number(toFiniteNumberOrNull(row.billability) ?? row.allocation ?? 0);
    }

    roleMap.set(role, entry);
  }

  const tbhItems = Array.isArray(tbhRows) ? tbhRows : [];
  for (const tbhRow of tbhItems) {
    if (!tbhRow || typeof tbhRow !== 'object') continue;

    const typeId = firstNonNullish(tbhRow.role_id, tbhRow.roleId, tbhRow.resource_type, tbhRow.resourceType, null);
    const resolvedRoleFromLookup = (
      typeId !== null
      && typeId !== undefined
      && resourceTypeLookup instanceof Map
        ? resourceTypeLookup.get(String(typeId))
        : null
    );
    const role = firstNonNullish(
      resolvedRoleFromLookup,
      toMeaningfulDisplayTextOrNull(tbhRow.role_name),
      toMeaningfulDisplayTextOrNull(tbhRow.roleName),
      toMeaningfulDisplayTextOrNull(typeId),
      '-',
    );

    const entry = roleMap.get(role) || {
      role,
      typeOrder: (
        resourceTypeOrderLookup instanceof Map
        && hasValue(typeId)
      )
        ? resourceTypeOrderLookup.get(String(typeId))
        : Number.MAX_SAFE_INTEGER,
      totalEmployees: new Set(),
      currentEmployees: new Set(),
      allocation: 0,
    };

    const tbhIdRaw = firstNonNullish(tbhRow.tbh_id, tbhRow.tbhId, tbhRow.id, null);
    const employeeIdentity = tbhIdRaw !== null ? `TBH_${tbhIdRaw}` : `TBH_${role}_${roleMap.size}`;
    entry.totalEmployees.add(String(employeeIdentity));

    const endDateRaw = firstNonNullish(tbhRow.allocation_end_date, tbhRow.allocationEndDate, tbhRow.end_date, tbhRow.endDate, null);
    const currentCountEligible = (
      isValidProjectDate(endDateRaw)
      && new Date(endDateRaw).getTime() >= today.getTime()
    );
    if (currentCountEligible) {
      entry.currentEmployees.add(String(employeeIdentity));
    }

    const startDateRaw = firstNonNullish(tbhRow.allocation_start_date, tbhRow.allocationStartDate, tbhRow.start_date, tbhRow.startDate, null);
    const allocationActive = (
      isValidProjectDate(startDateRaw)
      && isValidProjectDate(endDateRaw)
      && new Date(startDateRaw).getTime() <= today.getTime()
      && new Date(endDateRaw).getTime() >= today.getTime()
    );
    if (allocationActive) {
      const allocationPct = firstNonNullish(
        toFiniteNumberOrNull(tbhRow.allocation_pct),
        toFiniteNumberOrNull(tbhRow.allocationPct),
        toFiniteNumberOrNull(tbhRow.billability),
        toFiniteNumberOrNull(tbhRow.allocation),
        0,
      );
      entry.allocation += Number(allocationPct);
    }

    roleMap.set(role, entry);
  }

  return {
    resourceAllocationData: {
      summary: {
        total: allocations.length,
        open,
        closed,
        transferred: allocations.length - open - closed,
        allocated: allocations.length,
        days: Number(days.toFixed(2)),
        hours: Number(hours.toFixed(2)),
        overdue: 0,
      },
      allocations,
    },
    resourceCountData: Array.from(roleMap.values())
      .map((entry, i) => ({
        id: `resource_count_${i + 1}`,
        role: entry.role,
        order: entry.typeOrder,
        total: entry.totalEmployees.size,
        current: entry.currentEmployees.size,
        allocation: Number((entry.allocation / 100).toFixed(2)),
      }))
      .filter((entry) => entry.total !== 0 || entry.current !== 0),
  };
};

// ---------------------------------------------------------------------------
// CSAT helpers
// ---------------------------------------------------------------------------

const isDbCsatRow = (row) => Boolean(
  row && typeof row === 'object'
  && ('csat_id' in row || 'csat_rating' in row || 'csat_rec_date' in row || 'quality_of_service' in row),
);

const isDbCsatCustomerRow = (row) => Boolean(
  row && typeof row === 'object'
  && ('customer_id' in row || 'customer_name' in row || 'customer_email' in row),
);

const getRawCsatRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  return firstArray([
    payload.project_CSAT, payload.project_csat, payload.projectCsat, payload.projectCSAT,
    payload.projectCSATRows, payload.projectCSAT_rows, payload.csat, payload.csatRows,
    payload.csat_rows, payload.csatHistory, payload.csat_history,
  ]);
};

const getRawCsatCustomerRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  return firstArray([
    payload.csat_customer_details, payload.csatCustomerDetails, payload.csatCustomerDetailsRows,
    payload.csat_customer_details_rows, payload.customers, payload.customerDetails, payload.customer_details,
  ]);
};

const buildCsatCustomerLookup = (rows) => {
  const lookup = new Map();
  (Array.isArray(rows) ? rows : []).filter((row) => row && typeof row === 'object').filter(isDbCsatCustomerRow)
    .forEach((row) => {
      const id = row.customer_id;
      if (!hasValue(id)) return;
      lookup.set(String(id), { name: row.customer_name ?? '', email: row.customer_email ?? '', comments: row.comments ?? '', projectId: row.project_id ?? null });
    });
  return lookup;
};

const buildCsatViewModelFromDbRows = (rows, customerLookup = null) => {
  const activeRows = (Array.isArray(rows) ? rows : []).filter((row) => row && typeof row === 'object' && !row.is_delete);
  if (!activeRows.length) return { csatSummary: null, csatData: null, csatTableData: [] };

  const sortedByDateDesc = [...activeRows].sort((a, b) => {
    const at = isValidProjectDate(a.csat_rec_date) ? new Date(a.csat_rec_date).getTime() : 0;
    const bt = isValidProjectDate(b.csat_rec_date) ? new Date(b.csat_rec_date).getTime() : 0;
    return bt - at;
  });

  const latest = sortedByDateDesc[0] || {};
  const latestScore = toFiniteNumberOrNull(latest.csat_rating) ?? 0;
  const latestDateLabel = formatApiDate(latest.csat_rec_date);

  const overallScores = activeRows.map((r) => toFiniteNumberOrNull(r.csat_rating)).filter((v) => v !== null);
  const overallAverage = overallScores.length
    ? overallScores.reduce((s, v) => s + v, 0) / overallScores.length : 0;

  const currentFyLabel = getFiscalYearLabelForDate(latest.csat_rec_date) || getFiscalYearLabelForDate(new Date().toISOString());
  const currentFyRows = activeRows.filter((r) => getFiscalYearLabelForDate(r.csat_rec_date) === currentFyLabel);
  const currentFyScores = currentFyRows.map((r) => toFiniteNumberOrNull(r.csat_rating)).filter((v) => v !== null);
  const currentFyAverage = currentFyScores.length
    ? currentFyScores.reduce((s, v) => s + v, 0) / currentFyScores.length : 0;

  const daysAgo = calculateDaysAgo(latest.csat_rec_date);

  const getCustomerField = (row, field) => {
    const id = row.customer_id;
    if (!hasValue(id)) return field === 'name' ? '-' : '';
    const customer = customerLookup?.get(String(id));
    return field === 'name' ? (row.customer_name || customer?.name || String(id)) : (customer?.email || '');
  };

  return {
    csatSummary: { score: latestScore, date: latestDateLabel, responsesReceived: activeRows.length, averageCsat: Number(overallAverage.toFixed(1)), maxScore: 5 },
    csatData: {
      score: latestScore,
      date: latestDateLabel,
      overall: { total: activeRows.length, average: Number(overallAverage.toFixed(1)) },
      currentYear: { total: currentFyRows.length, average: Number(currentFyAverage.toFixed(1)), year: currentFyLabel || '' },
      lastReceived: daysAgo === null ? '-' : `${daysAgo} days ago`,
    },
    csatTableData: sortedByDateDesc.map((row) => ({
      id: row.csat_id ?? row.id ?? `${row.project_id || 'project'}_${row.csat_rec_date || 'csat'}`,
      date: row.csat_rec_date ?? row.date ?? '',
      receivedDate: formatApiDate(row.csat_rec_date),
      serviceQuality: toFiniteNumberOrNull(row.quality_of_service) ?? 0,
      roi: toFiniteNumberOrNull(row.return_on_investment) ?? 0,
      management: toFiniteNumberOrNull(row.management_communication) ?? 0,
      overall: toFiniteNumberOrNull(row.csat_rating) ?? 0,
      customerId: row.customer_id ?? null,
      customer: getCustomerField(row, 'name'),
      customerEmail: getCustomerField(row, 'email'),
      feedback: firstNonNullish(row.remarks, row.other_feedback, row.feedback, ''),
    })),
  };
};

// ---------------------------------------------------------------------------
// Audit helpers
// ---------------------------------------------------------------------------

const normalizeAuditCategoryKey = (value) => {
  const compact = String(value || '').toLowerCase()
    .replaceAll(RE_NON_ALPHA_LOWER, '');
  if (!compact) return null;
  if (compact.includes('process') || compact.includes('pmo')) return 'process';
  if (compact.includes('quality')) return 'quality';
  if (compact.includes('technology') || compact.includes('tech')) return 'technology';
  return null;
};

const isDbAuditDateRow = (row) => Boolean(row && typeof row === 'object' && ('audit_id' in row || 'audit_date' in row || 'project_id' in row));
const isAuditDatesNestedShape = (row) => Boolean(row && typeof row === 'object' && 'audit_date' in row && (Array.isArray(row.auditScore) || Array.isArray(row.audit_score) || Array.isArray(row.audit_scores)));
const isDbAuditNameRow = (row) => Boolean(row && typeof row === 'object' && ('audit_name_id' in row || 'audit_name' in row || 'audit_nick_name' in row));
const isDbAuditScoreRow = (row) => Boolean(row && typeof row === 'object' && ('score_id' in row || 'audit_id' in row || 'audit_name_id' in row || 'audit_scores' in row));

const parseAuditScoreValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const str = String(value).trim();
  if (str.toUpperCase() === 'NA') return 'NA';
  if (str === '-') return '-';
  const asNumber = toFiniteNumberOrNull(value);
  if (asNumber !== null) return asNumber;
  const match = RE_NUMBER_IN_STRING.exec(str);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

const computeAuditCategorySummary = (sortedHistory, categoryKey) => {
  const scored = sortedHistory
    .map((row) => ({ score: toFiniteNumberOrNull(row[`${categoryKey}Score`]), auditDate: row._auditDate }))
    .filter((e) => e.score !== null);
  const count = scored.length;
  const average = count ? scored.reduce((s, e) => s + (e.score || 0), 0) / count : 0;
  const latest = scored[0] || null;
  return { count, average: Number(average.toFixed(1)), lastAudit: latest ? (calculateDaysAgo(latest.auditDate) ?? 0) : 0 };
};

const getRawAuditDateRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload.audit ?? payload.auditData ?? payload.audit_details ?? payload.auditDetails ?? null;
  return firstArray([
    payload.audit_dates, payload.auditDates, payload.audit_date_rows, payload.audit_dates_rows, payload.auditDatesRows,
    nested?.audit_dates, nested?.auditDates, nested?.audit_date_rows, nested?.audit_dates_rows, nested?.auditDatesRows,
  ]);
};

const getRawAuditNameRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload.audit ?? payload.auditData ?? payload.audit_details ?? payload.auditDetails ?? null;
  return firstArray([
    payload.audit_names, payload.auditNames, payload.audit_name_rows, payload.audit_names_rows, payload.auditNamesRows,
    nested?.audit_names, nested?.auditNames, nested?.audit_name_rows, nested?.audit_names_rows, nested?.auditNamesRows,
  ]);
};

const getRawAuditScoreRowsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload.audit ?? payload.auditData ?? payload.audit_details ?? payload.auditDetails ?? null;
  return firstArray([
    payload.audit_score, payload.auditScore, payload.audit_scores, payload.auditScoreRows, payload.audit_score_rows, payload.audit_scores_rows,
    nested?.audit_score, nested?.auditScore, nested?.audit_scores, nested?.auditScoreRows, nested?.audit_score_rows, nested?.audit_scores_rows,
  ]);
};

const buildAuditViewModelFromAuditDates = (auditDates) => {
  const rows = (Array.isArray(auditDates) ? auditDates : [])
    .filter((row) => row && typeof row === 'object' && isValidProjectDate(row.audit_date));

  if (!rows.length) {
    return { auditSummary: null, auditScoreData: { process: { count: 0, average: 0, lastAudit: 0 }, quality: { count: 0, average: 0, lastAudit: 0 }, technology: { count: 0, average: 0, lastAudit: 0 }, history: [] } };
  }

  const history = rows.map((auditRow) => {
    const apiDate = auditRow.audit_date;
    const label = formatApiDate(apiDate);
    const day = getDayNameForApiDate(apiDate);
    const scores = { process: { value: null, auditNameId: null, scoreId: null }, quality: { value: null, auditNameId: null, scoreId: null }, technology: { value: null, auditNameId: null, scoreId: null } };

    const nestedScores = auditRow.auditScore || auditRow.audit_score || auditRow.audit_scores || [];
    (Array.isArray(nestedScores) ? nestedScores : []).forEach((scoreRow) => {
      if (!scoreRow || typeof scoreRow !== 'object') return;
      const nameObj = scoreRow.auditNames || scoreRow.audit_names || scoreRow.auditName || scoreRow.audit_name || null;
      const category = normalizeAuditCategoryKey(nameObj?.audit_nick_name || nameObj?.audit_name);
      if (!category) return;
      const score = parseAuditScoreValue(scoreRow.audit_scores ?? scoreRow.audit_score ?? scoreRow.score);
      if (score === null) return;
      scores[category] = { value: score, auditNameId: nameObj?.audit_name_id ?? nameObj?.auditNameId ?? scoreRow?.audit_name_id ?? null, scoreId: scoreRow?.score_id ?? scoreRow?.scoreId ?? null };
    });

    return {
      id: auditRow.audit_id ?? `${auditRow.project_id || 'project'}_${apiDate || 'audit'}`,
      row_id: auditRow.audit_id ?? `${auditRow.project_id || 'project'}_${apiDate || 'audit'}`,
      processDate: scores.process.value === null ? '' : label,
      processDay: scores.process.value === null ? '' : day,
      processScore: scores.process.value,
      processAuditNameId: scores.process.auditNameId,
      processScoreId: scores.process.scoreId,
      qualityDate: scores.quality.value === null ? '' : label,
      qualityDay: scores.quality.value === null ? '' : day,
      qualityScore: scores.quality.value,
      qualityAuditNameId: scores.quality.auditNameId,
      qualityScoreId: scores.quality.scoreId,
      technologyDate: scores.technology.value === null ? '' : label,
      technologyDay: scores.technology.value === null ? '' : day,
      technologyScore: scores.technology.value,
      technologyAuditNameId: scores.technology.auditNameId,
      technologyScoreId: scores.technology.scoreId,
      _auditDate: apiDate,
    };
  });

  const sortedHistory = [...history].sort((a, b) => new Date(b._auditDate).getTime() - new Date(a._auditDate).getTime());
  const process = computeAuditCategorySummary(sortedHistory, 'process');
  const quality = computeAuditCategorySummary(sortedHistory, 'quality');
  const technology = computeAuditCategorySummary(sortedHistory, 'technology');

  const latestProcessRow = sortedHistory.find((r) => toFiniteNumberOrNull(r.processScore) !== null) || null;
  const latestQualityRow = sortedHistory.find((r) => toFiniteNumberOrNull(r.qualityScore) !== null) || null;
  const latestTechRow = sortedHistory.find((r) => toFiniteNumberOrNull(r.technologyScore) !== null) || null;

  const auditSummary = (latestProcessRow || latestQualityRow || latestTechRow) ? {
    pmo: { score: toFiniteNumberOrNull(latestProcessRow?.processScore), date: latestProcessRow?.processDate || '-' },
    quality: { score: toFiniteNumberOrNull(latestQualityRow?.qualityScore), date: latestQualityRow?.qualityDate || '-' },
    tech: { score: toFiniteNumberOrNull(latestTechRow?.technologyScore), date: latestTechRow?.technologyDate || '-' },
  } : null;

  return {
    auditSummary,
    auditScoreData: {
      process,
      quality,
      technology,
      history: sortedHistory.map(({ _auditDate: __auditDate, ...rest }) => rest),
    },
  };
};

const buildAuditViewModelFromDbRows = ({ auditDates, auditNames, auditScores }) => {
  const empty = () => ({ auditSummary: null, auditScoreData: { process: { count: 0, average: 0, lastAudit: 0 }, quality: { count: 0, average: 0, lastAudit: 0 }, technology: { count: 0, average: 0, lastAudit: 0 }, history: [] } });

  const dateRows = (Array.isArray(auditDates) ? auditDates : []).filter((r) => r && typeof r === 'object').filter(isDbAuditDateRow)
    .filter((r) => isValidProjectDate(r.audit_date));
  const nameRows = (Array.isArray(auditNames) ? auditNames : []).filter((r) => r && typeof r === 'object').filter(isDbAuditNameRow);
  const scoreRows = (Array.isArray(auditScores) ? auditScores : []).filter((r) => r && typeof r === 'object').filter(isDbAuditScoreRow);

  if (!dateRows.length) return empty();

  if (!scoreRows.length || !nameRows.length) {
    const sorted = [...dateRows].sort((a, b) => new Date(b.audit_date).getTime() - new Date(a.audit_date).getTime());
    const latestDate = sorted[0]?.audit_date;
    const lastAudit = latestDate ? (calculateDaysAgo(latestDate) ?? 0) : 0;
    const scoreData = { count: 0, average: 0, lastAudit };
    return {
      auditSummary: {
        pmo: { score: null, date: latestDate ? formatApiDate(latestDate) : '-' },
        quality: { score: null, date: latestDate ? formatApiDate(latestDate) : '-' },
        tech: { score: null, date: latestDate ? formatApiDate(latestDate) : '-' },
      },
      auditScoreData: {
        process: scoreData,
        quality: scoreData,
        technology: scoreData,
        history: sorted.map((row) => ({
          id: row.audit_id ?? `${row.project_id || 'project'}_${row.audit_date || 'audit'}`,
          row_id: row.audit_id ?? `${row.project_id || 'project'}_${row.audit_date || 'audit'}`,
          processDate: formatApiDate(row.audit_date),
          processDay: getDayNameForApiDate(row.audit_date),
          processScore: null,
          processAuditNameId: null,
          processScoreId: null,
          qualityDate: formatApiDate(row.audit_date),
          qualityDay: getDayNameForApiDate(row.audit_date),
          qualityScore: null,
          qualityAuditNameId: null,
          qualityScoreId: null,
          technologyDate: formatApiDate(row.audit_date),
          technologyDay: getDayNameForApiDate(row.audit_date),
          technologyScore: null,
          technologyAuditNameId: null,
          technologyScoreId: null,
        })),
      },
    };
  }

  const auditDateById = new Map(dateRows.map((row) => [String(row.audit_id), row.audit_date]));
  const categoryByAuditNameId = new Map();
  nameRows.forEach((row) => {
    const id = row.audit_name_id;
    if (!hasValue(id)) return;
    const category = normalizeAuditCategoryKey(row.audit_nick_name || row.audit_name);
    if (category) categoryByAuditNameId.set(String(id), category);
  });

  const scoresByAuditId = new Map();
  scoreRows.forEach((row) => {
    const auditId = row.audit_id;
    const auditNameId = row.audit_name_id;
    if (!hasValue(auditId) || !hasValue(auditNameId)) return;
    const category = categoryByAuditNameId.get(String(auditNameId));
    if (!category) return;
    const score = parseAuditScoreValue(row.audit_scores);
    if (score === null) return;
    const key = String(auditId);
    scoresByAuditId.set(key, { ...scoresByAuditId.get(key), [category]: { value: score, auditNameId: row.audit_name_id ?? null, scoreId: row.score_id ?? row.scoreId ?? null } });
  });

  const history = [];
  scoresByAuditId.forEach((scores, auditId) => {
    const auditDate = auditDateById.get(String(auditId));
    if (!isValidProjectDate(auditDate)) return;
    const day = getDayNameForApiDate(auditDate);
    const label = formatApiDate(auditDate);
    history.push({
      id: auditId,
      row_id: auditId,
      processDate: scores.process === undefined ? '' : label,
      processDay: scores.process === undefined ? '' : day,
      processScore: scores.process?.value ?? 0,
      processAuditNameId: scores.process?.auditNameId ?? null,
      processScoreId: scores.process?.scoreId ?? null,
      qualityDate: scores.quality === undefined ? '' : label,
      qualityDay: scores.quality === undefined ? '' : day,
      qualityScore: scores.quality?.value ?? 0,
      qualityAuditNameId: scores.quality?.auditNameId ?? null,
      qualityScoreId: scores.quality?.scoreId ?? null,
      technologyDate: scores.technology === undefined ? '' : label,
      technologyDay: scores.technology === undefined ? '' : day,
      technologyScore: scores.technology?.value ?? 0,
      technologyAuditNameId: scores.technology?.auditNameId ?? null,
      technologyScoreId: scores.technology?.scoreId ?? null,
      _auditDate: auditDate,
    });
  });

  const sortedHistory = [...history].sort((a, b) => new Date(b._auditDate).getTime() - new Date(a._auditDate).getTime());
  const process = computeAuditCategorySummary(sortedHistory, 'process');
  const quality = computeAuditCategorySummary(sortedHistory, 'quality');
  const technology = computeAuditCategorySummary(sortedHistory, 'technology');

  const latestProcessRow = sortedHistory.find((r) => toFiniteNumberOrNull(r.processScore) !== null) || null;
  const latestQualityRow = sortedHistory.find((r) => toFiniteNumberOrNull(r.qualityScore) !== null) || null;
  const latestTechRow = sortedHistory.find((r) => toFiniteNumberOrNull(r.technologyScore) !== null) || null;
  const auditSummary = (!latestProcessRow && !latestQualityRow && !latestTechRow) ? null : {
    pmo: { score: toFiniteNumberOrNull(latestProcessRow?.processScore) ?? 0, date: latestProcessRow?.processDate || '-' },
    quality: { score: toFiniteNumberOrNull(latestQualityRow?.qualityScore) ?? 0, date: latestQualityRow?.qualityDate || '-' },
    tech: { score: toFiniteNumberOrNull(latestTechRow?.technologyScore) ?? 0, date: latestTechRow?.technologyDate || '-' },
  };

  return {
    auditSummary,
    auditScoreData: {
      process,
      quality,
      technology,
      history: sortedHistory.map(({ _auditDate: __auditDate, ...rest }) => rest),
    },
  };
};

// ---------------------------------------------------------------------------
// Billing type resolution
// ---------------------------------------------------------------------------

const getProjectBillingTypeText = (data, resolvedProjectType) => {
  const directLabel = firstNonNullish(
    toLookupDisplayTextOrNull(data?.billing_type_name),
    toLookupDisplayTextOrNull(data?.billingTypeName),
    toLookupDisplayTextOrNull(data?.billing_type_label),
    toLookupDisplayTextOrNull(data?.billingTypeLabel),
  );
  if (directLabel) return directLabel;

  if (isTimeAndMaterialProjectType(resolvedProjectType)) {
    const tmLabel = firstNonNullish(
      toLookupDisplayTextOrNull(data?.tmbillingtype?.tm_bill_type),
      toLookupDisplayTextOrNull(data?.tmbillingtype?.tm_bil_type),
      toLookupDisplayTextOrNull(data?.tm_billing_type?.tm_bill_type),
      toLookupDisplayTextOrNull(data?.tm_billing_type?.label),
      toLookupDisplayTextOrNull(data?.tmBillingType?.tmBillType),
      toLookupDisplayTextOrNull(data?.tmBillingType?.label),
    );
    if (tmLabel) return tmLabel;
  }

  if (isFixedPriceProjectType(resolvedProjectType)) {
    const fpLabel = firstNonNullish(
      toLookupDisplayTextOrNull(data?.fpbillingtype?.fp_bil_type),
      toLookupDisplayTextOrNull(data?.fpbillingtype?.fp_bill_type),
      toLookupDisplayTextOrNull(data?.fp_billing_type?.fp_bill_type),
      toLookupDisplayTextOrNull(data?.fp_billing_type?.label),
      toLookupDisplayTextOrNull(data?.fpBillingType?.fpBillType),
      toLookupDisplayTextOrNull(data?.fpBillingType?.label),
    );
    if (fpLabel) return fpLabel;
  }

  const genericLabel = firstNonNullish(
    toLookupDisplayTextOrNull(data?.billingType?.label),
    toLookupDisplayTextOrNull(data?.billingType?.name),
    toLookupDisplayTextOrNull(data?.billingType?.billing_type_name),
    toLookupDisplayTextOrNull(data?.billing_type?.label),
    toLookupDisplayTextOrNull(data?.billing_type?.name),
    toLookupDisplayTextOrNull(data?.billing_type?.billing_type_name),
    toLookupDisplayTextOrNull(data?.basicDetails?.billingType),
  );

  return genericLabel || '-';
};

const getProjectOfferingText = (data, projectRecord) => {
  const label = firstNonNullish(
    toLookupDisplayTextOrNull(data?.offering_name),
    typeof data?.projectOffering === 'string' ? toLookupDisplayTextOrNull(data.projectOffering) : null,
    toLookupDisplayTextOrNull(data?.projectOffering?.offering_name),
    toLookupDisplayTextOrNull(data?.projectOffering?.project_offering_name),
    toLookupDisplayTextOrNull(data?.basicDetails?.offering),
    hasValue(projectRecord?.project_offering) ? toLookupDisplayTextOrNull(String(projectRecord.project_offering)) : null,
  );
  return label || '-';
};

const getDeliveryMethodologyDirectoryMap = (baseData) => {
  const rows = Array.isArray(baseData?.deliveryMethodologyDirectory)
    ? baseData.deliveryMethodologyDirectory
    : [];

  return rows.reduce((acc, row) => {
    const id = toNumberOrNull(
      row?.method_id ?? row?.methodId ?? row?.delivery_methodology_id ?? row?.id,
    );
    const label = toLookupDisplayTextOrNull(
      row?.methodology ?? row?.delivery_methodology_name ?? row?.name ?? row?.label,
    );

    if (id !== null && label) {
      acc.set(id, label);
    }

    return acc;
  }, new Map());
};

const getProjectMethodologyTextFromDirectory = (data, baseData) => {
  const methodologyId = toNumberOrNull(
    data?.methodology ?? data?.delivery_methodology ?? baseData?.methodology ?? baseData?.delivery_methodology,
  );

  if (methodologyId === null) return null;

  return getDeliveryMethodologyDirectoryMap(baseData).get(methodologyId) || null;
};

// ---------------------------------------------------------------------------
// Project header helpers
// ---------------------------------------------------------------------------

const getProjectMethodologyText = (data, baseData = {}) => firstNonNullish(
  toLookupDisplayTextOrNull(data?.methodology_name),
  toLookupDisplayTextOrNull(data?.delivery_methodology_name),
  toLookupDisplayTextOrNull(data?.deliveryMethodology?.methodology),
  toLookupDisplayTextOrNull(data?.basicDetails?.methodology),
  getProjectMethodologyTextFromDirectory(data, baseData),
  typeof data?.methodology === 'string' ? toLookupDisplayTextOrNull(data.methodology) : null,
  '-',
);

const getProjectHeaderBasicDetailValue = (baseData, label) => {
  const basicDetails = Array.isArray(baseData?.projectHeader?.basicDetails) ? baseData.projectHeader.basicDetails : [];
  const match = basicDetails.find((item) => String(item?.label || '').trim()
    .toLowerCase() === String(label || '').trim()
    .toLowerCase());
  const matchedValue = toMeaningfulDisplayTextOrNull(match?.value);
  if (matchedValue) return matchedValue;

  const objectKeyMap = { vertical: 'vertical', type: 'type', 'billing type': 'billingType', offering: 'offering', methodology: 'methodology' };
  const mappedKey = objectKeyMap[String(label || '').trim()
    .toLowerCase()];
  return mappedKey ? toMeaningfulDisplayTextOrNull(baseData?.basicDetails?.[mappedKey]) : null;
};

const getProjectHeaderData = (projectRecord, baseData = {}) => ({
  ...(projectRecord && typeof projectRecord === 'object' ? projectRecord : {}),
  ...(baseData && typeof baseData === 'object' ? baseData : {}),
  ...(baseData?.data && typeof baseData.data === 'object' ? baseData.data : {}),
});

const getHeaderMetricsSources = (baseData) => ({
  effortMetricsDisplay: firstNonNullish(
    getNestedValue(baseData, 'effortMetrics.effortMetricsDisplay'),
    getNestedValue(baseData, 'data.effortMetrics.effortMetricsDisplay'),
    getNestedValue(baseData, 'effort_metrics.effort_metrics_display'),
    getNestedValue(baseData, 'data.effort_metrics.effort_metrics_display'),
  ),
  resourcesPayload: firstNonNullish(
    getNestedValue(baseData, 'resources'),
    getNestedValue(baseData, 'data.resources'),
    getObjectFromNestedPath(baseData, 'resources'),
    getObjectFromNestedPath(baseData, 'data.resources'),
  ),
});

const getHeaderResourceMetrics = (projectRecord, baseData) => {
  const { effortMetricsDisplay, resourcesPayload } = getHeaderMetricsSources(baseData);
  return {
    staffedValue: firstNonNullish(
      toDisplayTextOrNull(resourcesPayload?.staffed),
      toDisplayTextOrNull(resourcesPayload?.staffedHeadCount),
      toDisplayTextOrNull(baseData?.head_count),
      toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'head_count')),
      toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'resources.staffed')),
      hasValue(projectRecord?.no_resource) ? String(projectRecord.no_resource) : null,
      '-',
    ),
    effortLoggedOverallValue: firstNonNullish(
      toDisplayTextOrNull(effortMetricsDisplay?.effort_logged_overall_text),
      toDisplayTextOrNull(resourcesPayload?.effort),
      toDisplayTextOrNull(baseData?.logged_effort),
      toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'logged_effort')),
      hasValue(projectRecord?.efforts_hour) ? String(projectRecord.efforts_hour) : null,
      '-',
    ),
    idleTimeOverallValue: firstNonNullish(
      toDisplayTextOrNull(effortMetricsDisplay?.idle_time_overall_hours_text),
      toDisplayTextOrNull(resourcesPayload?.totalIdleTime),
      toDisplayTextOrNull(baseData?.totalIdleTime),
      toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'totalIdleTime')),
      toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'effortMetrics.idleTime.total_idle_time')),
      '-',
    ),
  };
};

const calculateDelay = (endDate, replannedEndDate, baselineStartDate) => {
  if (!isValidProjectDate(endDate) || !isValidProjectDate(replannedEndDate) || !isValidProjectDate(baselineStartDate)) return null;
  const days = Math.round((new Date(replannedEndDate).getTime() - new Date(endDate).getTime()) / 86_400_000);
  const duration = Math.round((new Date(endDate).getTime() - new Date(baselineStartDate).getTime()) / 86_400_000);
  if (duration > 0) {
    const percentage = Math.round((days / duration) * 100);
    return { days, percentage };
  }
  return { days, percentage: 0 };
};

const resolveTimelineDelay = ({
  endDate,
  replannedEndDate,
  baselineStartDate,
  explicitDelay,
}) => {
  const calculatedDelay = calculateDelay(endDate, replannedEndDate, baselineStartDate);
  if (calculatedDelay) return calculatedDelay;

  if (!hasValue(explicitDelay)) return null;

  return {
    days: toFiniteNumberOrNull(explicitDelay) ?? 0,
    percentage: 0,
  };
};

const buildProjectHeaderFromProjectRecord = (projectRecord, baseData = {}) => {
  const data = getProjectHeaderData(projectRecord, baseData);

  const verticalText = firstNonNullish(
    getProjectHeaderBasicDetailValue(baseData, 'Vertical'),
    data?.project_vertical,
    data?.project_vertical_name,
    data?.vertical,
    data?.vertical_label,
    typeof data?.vertical_name === 'string' ? data.vertical_name : null,
    data?.vertical_name?.vertical,
    '-',
  );

  const resolvedProjectType = resolveProjectTypeValue(
    getProjectHeaderBasicDetailValue(baseData, 'Type')
    || data?.project_type_name || data?.project_type || data?.type
    || data?.projectType?.project_type_name
    || data?.effort_type
    || data?.project_type_id
    || data?.projectType?.project_type_id
    || data?.projectTypeId
    || '',
  );

  const typeText = firstNonNullish(
    getProjectHeaderBasicDetailValue(baseData, 'Type'),
    data?.project_type_name,
    data?.project_type || data?.type || data?.projectType?.project_type_name || data?.effort_type || resolvedProjectType,
    '-',
  );

  const billingTypeText = firstNonNullish(
    getProjectBillingTypeText(data, resolvedProjectType),
    getProjectHeaderBasicDetailValue(baseData, 'Billing type'),
    '-',
  );

  const offeringText = firstNonNullish(
    getProjectHeaderBasicDetailValue(baseData, 'Offering'),
    getProjectOfferingText(data, projectRecord),
    '-',
  );

  const methodologyText = firstNonNullish(
    getProjectHeaderBasicDetailValue(baseData, 'Methodology'),
    getProjectMethodologyText(data, baseData),
    '-',
  );
  const { staffedValue, effortLoggedOverallValue, idleTimeOverallValue } = getHeaderResourceMetrics(projectRecord, baseData);
  const resolvedEndDate = firstNonNullish(
    projectRecord?.end_date,
    data?.end_date,
    data?.project?.end_date,
    data?.timeline?.end_date,
  );
  const resolvedReplannedEndDate = firstNonNullish(
    projectRecord?.replanned_end_date,
    data?.replanned_end_date,
    data?.project?.replanned_end_date,
    data?.timeline?.replanned_end_date,
  );

  const resolvedStartDate = firstNonNullish(
    projectRecord?.start_date,
    data?.start_date,
    data?.project?.start_date,
    data?.timeline?.start_date,
  );

  const delay = resolveTimelineDelay({
    endDate: resolvedEndDate,
    replannedEndDate: resolvedReplannedEndDate,
    baselineStartDate: resolvedStartDate,
    explicitDelay: data?.timeline?.delay,
  });

  return {
    title: projectRecord?.project_name || data?.project?.projectName || '-',
    status: projectRecord?.project_status || data?.project?.status || '-',
    statusTextColor: projectRecord?.status_color || projectRecord?.projectStatus?.status_color || '',
    statusBgColor: projectRecord?.status_box_color || projectRecord?.projectStatus?.status_box_color || '',
    ragStatusColor: (
      projectRecord?.overviewAccentColor
      || projectRecord?.health
      || projectRecord?.rag_status_color
      || data?.overviewAccentColor
      || data?.project?.overviewAccentColor
      || data?.health
      || data?.project?.health
      || ''
    ),
    client: projectRecord?.client_name || data?.project?.clientName || (projectRecord?.client_id ? String(projectRecord.client_id) : '-'),
    timeline: {
      rlsd: normalizeDisplayDateValue(data?.timeline?.start_date, formatApiDate(projectRecord?.start_date)),
      rled: normalizeDisplayDateValue(data?.timeline?.end_date, formatApiDate(projectRecord?.end_date)),
      acsd: normalizeDisplayDateValue(data?.timeline?.actual_start_date, formatApiDate(projectRecord?.actual_start_date)),
      rped: normalizeDisplayDateValue(data?.timeline?.replanned_end_date, formatApiDate(projectRecord?.replanned_end_date)),
      delay,
      startDateRaw: projectRecord?.start_date || '',
      endDateRaw: resolvedEndDate || '',
      actualStartDateRaw: projectRecord?.actual_start_date || '',
      replannedEndDateRaw: resolvedReplannedEndDate || '',
      projectType: resolvedProjectType || '',
      projectStatus: projectRecord?.project_status || data?.project?.status || '',
    },
    basicDetails: [
      { icon: VerticalIcon, label: 'Vertical', value: verticalText },
      { icon: TypeIcon, label: 'Type', value: typeText },
      { icon: BillingIcon, label: 'Billing type', value: billingTypeText },
      { icon: OfferingIcon, label: 'Offering', value: offeringText },
      { icon: MethodologyIcon, label: 'Methodology', value: methodologyText },
    ],
    resources: [
      { icon: StaffedIcon, label: 'Staffed', value: staffedValue },
      { icon: OverallEffortIcon, label: 'Effort logged (Overall)', value: effortLoggedOverallValue },
      { icon: OverallIdleIcon, label: 'Idle time in hrs (Overall)', value: idleTimeOverallValue },
    ],
    progress: [],
  };
};

// ---------------------------------------------------------------------------
// Progress summary
// ---------------------------------------------------------------------------

const buildProgressSummary = (baseData) => {
  const rawProgressRows = getRawProgressRowsFromPayload(baseData);
  const progressHydrated = rawProgressRows?.some(isDbProgressRow)
    ? buildProgressViewModelFromDbRows(rawProgressRows)
    : [];
  const highestProgressRow = progressHydrated.reduce((bestRow, currentRow) => {
    if (!currentRow) return bestRow;
    if (!bestRow) return currentRow;

    const currentProgress = toFiniteNumberOrNull(currentRow.progress) ?? Number.NEGATIVE_INFINITY;
    const bestProgress = toFiniteNumberOrNull(bestRow.progress) ?? Number.NEGATIVE_INFINITY;

    if (currentProgress > bestProgress) return currentRow;
    if (currentProgress < bestProgress) return bestRow;

    const currentDateScore = parseDisplayMetricDate(currentRow.date);
    const bestDateScore = parseDisplayMetricDate(bestRow.date);
    return currentDateScore > bestDateScore ? currentRow : bestRow;
  }, null);

  const jiraProgressValue = firstNonNullish(
    toDisplayTextOrNull(getNestedValue(baseData, 'jira.jira')),
    toDisplayTextOrNull(getNestedValue(baseData, 'data.jira.jira')),
    toDisplayTextOrNull(getNestedValue(baseData, 'jiraSummary.jiraProgressText')),
    toDisplayTextOrNull(getNestedValue(baseData, 'data.jiraSummary.jiraProgressText')),
    toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'pmoData.jira_progress')),
    '-',
  );

  const projectProgressValue = firstNonNullish(
    toDisplayTextOrNull(getNestedValue(baseData, 'jira.projectProgress')),
    toDisplayTextOrNull(getNestedValue(baseData, 'data.jira.projectProgress')),
    toDisplayTextOrNull(getNestedValue(baseData, 'jiraSummary.projectProgressText')),
    toDisplayTextOrNull(getNestedValue(baseData, 'data.jiraSummary.projectProgressText')),
    toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'projectProgress.progress_percent')),
    '-',
  );

  const projectProgressSortValue = firstNonNullish(
    toFiniteNumberOrNull(getNestedValue(baseData, 'jira.projectProgressValue')),
    toFiniteNumberOrNull(getNestedValue(baseData, 'data.jira.projectProgressValue')),
    toFiniteNumberOrNull(getNestedValue(baseData, 'jiraSummary.projectProgressValue')),
    toFiniteNumberOrNull(getNestedValue(baseData, 'data.jiraSummary.projectProgressValue')),
    toFiniteNumberOrNull(getDirectOrNestedValue(baseData, 'projectProgress.progress_percent_value')),
    null,
  );

  const projectProgressDisplay = splitMetricDisplayText(projectProgressValue);
  let progressSummaryLabel = 'Progress';
  if (highestProgressRow?.date) {
    progressSummaryLabel = `Progress (On ${highestProgressRow.date})`;
  } else if (projectProgressDisplay.date) {
    progressSummaryLabel = `Progress (On ${projectProgressDisplay.date})`;
  }

  const progressSummaryValue = highestProgressRow
    ? `${Number(highestProgressRow.progress || 0).toFixed(2)}%`
    : (projectProgressDisplay.value || projectProgressValue);

  return {
    progressHydrated,
    progressSummaryLabel,
    progressSummaryValue,
    progressSummarySortValue: highestProgressRow?.progress ?? projectProgressSortValue,
    jiraSummaryValue: jiraProgressValue,
  };
};

// ---------------------------------------------------------------------------
// Utilization / Revenue / GM
// ---------------------------------------------------------------------------

const buildUtilizationData = (baseData) => {
  const estimationVsActual = firstNonNullish(
    getNestedValue(baseData, 'effortMetrics.estimationVsActual'),
    getNestedValue(baseData, 'data.effortMetrics.estimationVsActual'),
  );
  const overallUtilization = firstNonNullish(
    getNestedValue(baseData, 'effortMetrics.estimateOverallUtilization'),
    getNestedValue(baseData, 'data.effortMetrics.estimateOverallUtilization'),
  );
  const latestMonthUtilization = firstNonNullish(
    getNestedValue(baseData, 'effortMetrics.latestMonthUtilization'),
    getNestedValue(baseData, 'data.effortMetrics.latestMonthUtilization'),
  );
  const crSummary = firstNonNullish(getNestedValue(baseData, 'crSummary'), getNestedValue(baseData, 'data.crSummary'));
  const projectTypeVariant = firstNonNullish(
    normalizeProjectTypeVariant(baseData?.effort_type),
    normalizeProjectTypeVariant(baseData?.project_type),
    normalizeProjectTypeVariant(baseData?.projectType?.project_type_name),
    normalizeProjectTypeVariant(getDirectOrNestedValue(baseData, 'data.effort_type')),
    normalizeProjectTypeVariant(getDirectOrNestedValue(baseData, 'data.project_type')),
    '',
  );
  if (!estimationVsActual && !overallUtilization && !latestMonthUtilization && !crSummary && !projectTypeVariant) return null;

  const initialEstEffort = toFiniteNumberOrNull(estimationVsActual?.total_initial_estimation);
  const approvedCREffort = firstNonNullish(
    toFiniteNumberOrNull(crSummary?.approved_efforts),
    toFiniteNumberOrNull(baseData?.approved_efforts),
    toFiniteNumberOrNull(getNestedValue(baseData, 'data.approved_efforts')),
    0,
  );
  const latestMonthUtilizationDisplay = firstNonNullish(
    toDisplayTextOrNull(latestMonthUtilization?.billability_value),
    toDisplayTextOrNull(latestMonthUtilization?.billability_formatted),
    null,
  );
  const latestMonthUtilizationSplit = splitMetricDisplayText(latestMonthUtilizationDisplay);

  return {
    mode: projectTypeVariant || (overallUtilization || latestMonthUtilization ? 'tm' : 'fp'),
    initialEstEffort,
    approvedCREffort,
    approvedCRPercentage: firstNonNullish(
      toFiniteNumberOrNull(crSummary?.cr_percent),
      toFiniteNumberOrNull(baseData?.cr_percent),
      toFiniteNumberOrNull(getNestedValue(baseData, 'data.cr_percent')),
      0,
    ),
    totalEstEffort: firstNonNullish(
      toFiniteNumberOrNull(crSummary?.total_efforts),
      toFiniteNumberOrNull(baseData?.total_efforts),
      toFiniteNumberOrNull(getNestedValue(baseData, 'data.total_efforts')),
      initialEstEffort !== null ? initialEstEffort + approvedCREffort : null,
    ),
    currentActEffort: toFiniteNumberOrNull(estimationVsActual?.current_actual_consumed_effort),
    estEffortDeviation: toFiniteNumberOrNull(estimationVsActual?.estimate_deviation),
    overallUtilization: firstNonNullish(
      toPercentOrNull(overallUtilization?.billability_percent),
      toPercentOrNull(baseData?.overall_utilization),
      0,
    ),
    latestUtilizationPercentage: firstNonNullish(
      toPercentOrNull(latestMonthUtilization?.billability_percent),
      toPercentOrNull(latestMonthUtilizationSplit.value),
      toPercentOrNull(latestMonthUtilization?.billability_value),
      0,
    ),
    latestUtilizationDate: firstNonNullish(
      formatMetricDateOrNull(latestMonthUtilization?.updated_date),
      latestMonthUtilizationSplit.date,
      null,
    ),
  };
};

const resolveRevenueData = (baseData) => {
  const revenueSummary = firstNonNullish(
    getObjectFromNestedPath(baseData, 'revenueSummary'), getObjectFromNestedPath(baseData, 'revenue_summary'), getObjectFromNestedPath(baseData, 'data.revenueSummary'), getObjectFromNestedPath(baseData, 'data.revenue_summary'),
  );
  return {
    revenueData: {
      totalRevenue: firstNonNullish(toFiniteNumberOrNull(revenueSummary?.total_revenue), toFiniteNumberOrNull(baseData?.total_revenue), toFiniteNumberOrNull(baseData?.total_revenue_value), toFiniteNumberOrNull(getDirectOrNestedValue(baseData, 'revenueSummary.total_revenue')), 0),
      invoiceAmount: firstNonNullish(toFiniteNumberOrNull(revenueSummary?.total_invoice_amount), toFiniteNumberOrNull(baseData?.total_invoice_amount), toFiniteNumberOrNull(baseData?.total_invoice_amount_value), toFiniteNumberOrNull(getDirectOrNestedValue(baseData, 'revenueSummary.total_invoice_amount')), 0),
      milestonesCompleted: firstNonNullish(
        toFiniteNumberOrNull(revenueSummary?.milestones_completed),
        toFiniteNumberOrNull(revenueSummary?.milestonesCompleted),
        toFiniteNumberOrNull(baseData?.milestones_completed),
        toFiniteNumberOrNull(baseData?.milestonesCompleted),
        toFiniteNumberOrNull(getDirectOrNestedValue(baseData, 'revenueSummary.milestones_completed')),
        toFiniteNumberOrNull(getDirectOrNestedValue(baseData, 'revenueSummary.milestonesCompleted')),
        null,
      ),
      totalMilestones: firstNonNullish(
        toFiniteNumberOrNull(revenueSummary?.total_milestones),
        toFiniteNumberOrNull(revenueSummary?.totalMilestones),
        toFiniteNumberOrNull(baseData?.total_milestones),
        toFiniteNumberOrNull(baseData?.totalMilestones),
        toFiniteNumberOrNull(getDirectOrNestedValue(baseData, 'revenueSummary.total_milestones')),
        toFiniteNumberOrNull(getDirectOrNestedValue(baseData, 'revenueSummary.totalMilestones')),
        null,
      ),
    },
  };
};

const resolveGmSummary = (baseData) => firstNonNullish(
  getObjectFromNestedPath(baseData, 'gmSummary'), getObjectFromNestedPath(baseData, 'gm_summary'), getObjectFromNestedPath(baseData, 'data.gmSummary'), getObjectFromNestedPath(baseData, 'data.gm_summary'),
);

const resolveAgnosticGmRow = (explicitValue, candidate, fallbackRows, fallbackDateKey) => {
  if (Array.isArray(explicitValue)) return explicitValue[0] ?? null;
  if (explicitValue && typeof explicitValue === 'object') return explicitValue;
  if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) return candidate;
  if (Array.isArray(candidate)) return pickLatestRowByDateKey(candidate, fallbackDateKey);
  return pickLatestRowByDateKey(fallbackRows, fallbackDateKey);
};

const getProjectIdCandidatesForGm = (baseData, projectRecord) => {
  const candidates = [
    projectRecord?.project_id,
    projectRecord?.project_seq_id,
    baseData?.project_id,
    baseData?.project_seq_id,
    getNestedValue(baseData, 'data.project_id'),
    getNestedValue(baseData, 'data.project_seq_id'),
  ]
    .map((value) => toNumberOrNull(value))
    .filter((value, index, arr) => value !== null && arr.indexOf(value) === index);

  return candidates;
};

const resolveLatestEstimatedGmRowForProject = (baseData, projectRecord, gmSummary) => {
  const projectIds = getProjectIdCandidatesForGm(baseData, projectRecord);
  const estimatedGmRows = firstNonNullish(
    getNestedValue(baseData, 'estimated_gm'),
    getNestedValue(baseData, 'data.estimated_gm'),
    getNestedValue(gmSummary, 'estimated_gm'),
    [],
  );

  const allRows = (Array.isArray(estimatedGmRows) ? estimatedGmRows : [])
    .filter((row) => {
      if (!row || typeof row !== 'object') return false;
      if (!projectIds.length) return true;
      const rowProjectId = toNumberOrNull(row?.project_id);
      return rowProjectId !== null && projectIds.includes(rowProjectId);
    });

  return pickLatestRowByDateKey(allRows.length ? allRows : estimatedGmRows, 'updated_on');
};

const resolveGmData = (baseData, projectRecord) => {
  const gmSummary = resolveGmSummary(baseData);
  const grossMarginRows = firstNonNullish(
    getNestedValue(baseData, 'gross_margin_data'),
    getNestedValue(baseData, 'data.gross_margin_data'),
    getNestedValue(gmSummary, 'gross_margin_data'),
  );
  const estimatedGmRows = firstNonNullish(
    getNestedValue(baseData, 'estimated_gm'),
    getNestedValue(baseData, 'data.estimated_gm'),
    getNestedValue(gmSummary, 'estimated_gm'),
  );
  const agmValue = firstNonNullish(
    getNestedValue(baseData, 'agmValue'),
    getNestedValue(baseData, 'agm_value'),
    getNestedValue(baseData, 'data.agmValue'),
    getNestedValue(baseData, 'data.agm_value'),
    getNestedValue(gmSummary, 'agmValue'),
    getNestedValue(gmSummary, 'agm_value'),
    gmSummary?.agm,
    getNestedValue(projectRecord, 'agmValue'),
  );
  const egmValue = firstNonNullish(
    getNestedValue(baseData, 'egmValue'),
    getNestedValue(baseData, 'egm_value'),
    getNestedValue(baseData, 'data.egmValue'),
    getNestedValue(baseData, 'data.egm_value'),
    getNestedValue(gmSummary, 'egmValue'),
    getNestedValue(gmSummary, 'egm_value'),
    gmSummary?.egm,
    getNestedValue(projectRecord, 'egmValue'),
  );

  const gmAgmRow = resolveAgnosticGmRow(agmValue, firstNonNullish(gmSummary?.agm, gmSummary?.AGM), grossMarginRows, 'month_end_date');
  const gmEgmRow = firstNonNullish(
    resolveLatestEstimatedGmRowForProject(baseData, projectRecord, gmSummary),
    resolveAgnosticGmRow(
      egmValue,
      firstNonNullish(gmSummary?.egm, gmSummary?.EGM),
      estimatedGmRows,
      'updated_on',
    ),
  );

  const sgmPercent = firstValidGMNumber(baseData?.sales_gross_margin, getNestedValue(baseData, 'data.sales_gross_margin'), projectRecord?.sales_gross_margin, gmSummary?.sales_gross_margin, gmSummary?.salesGrossMargin, gmSummary?.sgm);
  const agmPercent = firstValidGMNumberOrNull(
    getDirectOrNestedValue(baseData, 'agmValue.gm_percent'),
    getDirectOrNestedValue(baseData, 'agmValue.gmPercent'),
    getDirectOrNestedValue(baseData, 'agm_gm_percent'),
    getDirectOrNestedValue(baseData, 'gmSummary.agm.gm_percent'),
    getPercentFromRow(gmAgmRow),
  );
  const egmPercent = firstValidGMNumberOrNull(
    getPercentFromRow(gmEgmRow),
    getDirectOrNestedValue(baseData, 'egmValue.gm_percent'),
    getDirectOrNestedValue(baseData, 'egmValue.gmPercent'),
    getDirectOrNestedValue(baseData, 'egm_gm_percent'),
    getDirectOrNestedValue(baseData, 'gmSummary.egm.gm_percent'),
  );
  const agmDate = getDateFromRow(gmAgmRow, 'month_end_date', 'monthEndDate', 'month_end', 'monthEnd');
  const egmDate = getDateFromRow(gmEgmRow, 'updated_on', 'updatedOn', 'updated_at', 'updatedAt');

  return [
    { label: 'SGM', color: '#82E5B9', value: sgmPercent },
    { label: agmDate ? `AGM (On ${agmDate})` : 'AGM', color: '#FFD465', value: agmPercent },
    { label: egmDate ? `EGM (On ${egmDate})` : 'EGM', color: '#FF9A9A', value: egmPercent },
  ].filter((item) => item.value !== null);
};

// ---------------------------------------------------------------------------
// normalizeProjectRecord
// ---------------------------------------------------------------------------

const normalizeProjectRecord = (project) => ({
  project_seq_id: project?.project_seq_id ?? project?.project_id ?? null,
  project_id: project?.project_id ?? project?.project_seq_id ?? null,
  redmine_id: project?.redmine_id ?? 0,
  project_name: project?.project_name ?? '',
  project_manager: project?.project_manager ?? '',
  manager_id: project?.manager_id ?? '',
  prog_manager_id: project?.prog_manager_id ?? '',
  opportunity_id: project?.opportunity_id ?? '',
  project_description: project?.project_description ?? '',
  efforts_hour: project?.efforts_hour ?? null,
  finance_approval: project?.finance_approval ?? '',
  delivery_approval: project?.delivery_approval ?? '',
  risks_issues: project?.risks_issues ?? '',
  gp_precent: toNumberOrNull(project?.gp_precent) ?? 0,
  no_resource: toNumberOrNull(project?.no_resource),
  start_date: project?.start_date ?? API_DATE_PLACEHOLDER,
  end_date: project?.end_date ?? API_DATE_PLACEHOLDER,
  project_duration: project?.project_duration ?? null,
  estimated_month: toNumberOrNull(project?.estimated_month) ?? 0,
  effort_type: project?.effort_type ?? 'H',
  po_number: project?.po_number ?? null,
  project_type: project?.project_type ?? '',
  remarks: project?.remarks ?? null,
  project_close: Boolean(project?.project_close),
  project_vertical: project?.project_vertical ?? '',
  ...(Array.from({ length: 14 }, (_, index) => index + 2).reduce((acc, i) => ({ ...acc, [`manager_${i}`]: project?.[`manager_${i}`] ?? '', [`manager_${i}_name`]: project?.[`manager_${i}_name`] ?? '' }), {})),
  manager_id_name: project?.manager_id_name ?? project?.manager_1_name ?? '',
  ...(Array.from({ length: 15 }, (_, i) => i + 1).reduce((acc, i) => ({ ...acc, [`manager${i}`]: project?.[`manager${i}`] ?? '', [`manager${i}Id`]: project?.[`manager${i}Id`] ?? '' }), {})),
  project_status: project?.project_status ?? '',
  status_color: project?.status_color ?? project?.projectStatus?.status_color ?? '',
  status_box_color: project?.status_box_color ?? project?.projectStatus?.status_box_color ?? '',
  health: project?.health ?? project?.rag_status_color ?? project?.pmo_status_color ?? project?.project?.health ?? project?.project?.rag_status_color ?? '',
  overviewAccentColor: project?.overviewAccentColor ?? project?.overview_accent_color ?? project?.project?.overviewAccentColor ?? project?.project?.overview_accent_color ?? project?.health ?? project?.rag_status_color ?? project?.pmo_status_color ?? project?.project?.health ?? project?.project?.rag_status_color ?? '',
  actual_start_date: project?.actual_start_date ?? API_DATE_PLACEHOLDER,
  replanned_end_date: project?.replanned_end_date ?? API_DATE_PLACEHOLDER,
  opp_id: toNumberOrNull(project?.opp_id) ?? 0,
  client_id: project?.client_id ?? 0,
  business_requirements: project?.business_requirements ?? '',
  methodology: toNumberOrNull(project?.methodology) ?? 0,
  billing_type: toNumberOrNull(project?.billing_type) ?? 0,
  project_sponsor: toNumberOrNull(project?.project_sponsor) ?? 0,
  project_partner: toNumberOrNull(project?.project_partner) ?? 0,
  prog_manager_num: toNumberOrNull(project?.prog_manager_num) ?? 0,
  success_metrics: project?.success_metrics ?? '',
  project_offering: project?.project_offering ?? '',
  project_architects: project?.project_architects ?? '',
  project_sr_architect: project?.project_sr_architect ?? '',
  project_sr_architects: project?.project_sr_architects ?? '',
  project_senior_architect: project?.project_senior_architect ?? '',
  sr_manager_display: project?.sr_manager_display ?? '',
  sr_manager_details: project?.sr_manager_details ?? null,
  sr_manager_by_id: project?.sr_manager_by_id ?? null,
  sr_manager_by_number: project?.sr_manager_by_number ?? null,
  manager_details: project?.manager_details ?? [],
  manager_display: project?.manager_display ?? '',
  project_architects_display: project?.project_architects_display ?? '',
  project_architects_details: project?.project_architects_details ?? [],
  project_sr_architects_display: project?.project_sr_architects_display ?? '',
  project_sr_architects_details: project?.project_sr_architects_details ?? [],
  sales_gross_margin: project?.sales_gross_margin ?? '-',
  jira_project_id: project?.jira_project_id ?? '',
  vg_nvg_id: toNumberOrNull(project?.vg_nvg_id) ?? 0,
  effort_logging_in_darpan: toNumberOrNull(project?.effort_logging_in_darpan) ?? 0,
  project_dl: project?.project_dl ?? '',
  jira_link: project?.jira_link ?? '',
  drive_link: project?.drive_link ?? '',
  customer_type_id: toNumberOrNull(project?.customer_type_id) ?? 1,
  jira_easy_by_id: project?.jira_easy_by_id ?? '0',
  sub_billing_account: project?.sub_billing_account ?? '',
  billing_id: project?.billing_id ?? '',
  revenue_split_id: toNumberOrNull(project?.revenue_split_id) ?? 0,
  cost_split_id: toNumberOrNull(project?.cost_split_id) ?? 0,
  consumption_id: toNumberOrNull(project?.consumption_id) ?? 0,
  sales_vertical_id: toNumberOrNull(project?.sales_vertical_id) ?? 0,
  billing_entity_id: toNumberOrNull(project?.billing_entity_id) ?? 0,
  vertical_id: toNumberOrNull(project?.vertical_id) ?? 0,
  project_status_id: toNumberOrNull(project?.project_status_id) ?? 0,
  project_type_id: toNumberOrNull(project?.project_type_id) ?? 0,
  region_id: toNumberOrNull(project?.region_id) ?? 0,
  country_id: toNumberOrNull(project?.country_id) ?? 0,
  master_project_id: toNumberOrNull(project?.master_project_id) ?? 0,
  delivery_manager_id: project?.delivery_manager_id ?? '',
  client_name: project?.client?.client_name ?? '',
});

// ---------------------------------------------------------------------------
// Composite view-model builders
// ---------------------------------------------------------------------------

const getResponseData = (response, context) => {
  const payload = response?.data;
  if (!payload || payload?.name === 'AxiosError' || payload?.isAxiosError) throw new Error(`Failed to fetch ${context}`);
  if (typeof payload === 'object' && payload !== null && payload.success === false) throw new Error(payload.message || `Failed to fetch ${context}`);
  return payload?.data ?? payload;
};

const applyProjectDetailsToViewModel = (baseData, projectRecord) => {
  const resolvedHeader = buildProjectHeaderFromProjectRecord(projectRecord, baseData);
  const employeeDirectoryMap = getEmployeeDirectoryMap(baseData);

  const srManagerByEmpNo = getEmployeeFromDirectory(
    employeeDirectoryMap,
    firstNonNullish(
      toDisplayTextOrNull(projectRecord?.prog_manager_num),
      toDisplayTextOrNull(getNestedValue(baseData, 'data.prog_manager_num')),
      null,
    ),
    'emp_no',
  );
  const srManagerDetails = enrichPersonDetailsFromDirectory(firstNonNullish(
    srManagerByEmpNo,
    projectRecord?.sr_manager_details,
    projectRecord?.sr_manager_by_id,
    projectRecord?.sr_manager_by_number,
    getNestedValue(baseData, 'data.sr_manager_details'),
    getNestedValue(baseData, 'data.sr_manager_by_id'),
    getNestedValue(baseData, 'data.sr_manager_by_number'),
    null,
  ), employeeDirectoryMap, 'emp_no');
  const srManagerDisplay = firstNonNullish(
    srManagerDetails?.name,
    toDisplayTextOrNull(projectRecord?.sr_manager_display),
    toDisplayTextOrNull(getNestedValue(baseData, 'data.sr_manager_display')),
    null,
  );
  const projectArchitectsDisplay = firstNonNullish(projectRecord?.project_architects_display, getNestedValue(baseData, 'data.project_architects_display'), null);
  const projectArchitectsDetails = firstNonNullish(projectRecord?.project_architects_details, getNestedValue(baseData, 'data.project_architects_details'), []);
  const projectSrArchitectsDisplay = firstNonNullish(projectRecord?.project_sr_architects_display, getNestedValue(baseData, 'data.project_sr_architects_display'), null);
  const projectSrArchitectsDetails = firstNonNullish(projectRecord?.project_sr_architects_details, getNestedValue(baseData, 'data.project_sr_architects_details'), []);
  const srArchitectRawValue = firstNonNullish(
    projectRecord?.project_sr_architects,
    projectRecord?.project_sr_architect,
    projectRecord?.project_senior_architect,
    getNestedValue(baseData, 'data.project_sr_architects'),
    getNestedValue(baseData, 'data.project_sr_architect'),
    getNestedValue(baseData, 'data.project_senior_architect'),
    null,
  );
  const architectRawValue = firstNonNullish(
    projectRecord?.project_architects,
    getNestedValue(baseData, 'data.project_architects'),
    null,
  );

  const architectRows = (
    (() => {
      const rowsFromIds = buildPeopleRowsFromEmployeeRefs(
        architectRawValue,
        employeeDirectoryMap,
        'emp_no',
        true,
      );

      const seenKeys = new Set(
        rowsFromIds.map((r) => String(r.details?.emp_id || r.details?.emp_no || r.name || '').toLowerCase()).filter(Boolean),
      );
      const extraFromDetails = (Array.isArray(projectArchitectsDetails) ? projectArchitectsDetails : [])
        .map((row) => enrichPersonDetailsFromDirectory(row, employeeDirectoryMap, 'emp_no'))
        .filter(Boolean)
        .filter((detail) => {
          const key = String(detail.emp_id || detail.emp_no || detail.name || '').toLowerCase();
          return key && !seenKeys.has(key);
        })
        .map((detail) => ({ name: detail.name || '-', details: detail, isSenior: false }));

      const merged = [...rowsFromIds, ...extraFromDetails];
      if (merged.length > 0) return merged;

      return buildPeopleRows(
        projectArchitectsDisplay,
        projectArchitectsDetails,
        employeeDirectoryMap,
        'emp_no',
      ).map((row, index) => ({ ...row, isSenior: index === 0 }));
    })()
  );
  const srArchitectRows = (
    (() => {
      const rowsFromIds = buildPeopleRowsFromEmployeeRefs(
        srArchitectRawValue,
        employeeDirectoryMap,
        'emp_no',
        true,
      );

      const seenKeys = new Set(
        rowsFromIds.map((r) => String(r.details?.emp_id || r.details?.emp_no || r.name || '').toLowerCase()).filter(Boolean),
      );
      const extraFromDetails = (Array.isArray(projectSrArchitectsDetails) ? projectSrArchitectsDetails : [])
        .map((row) => enrichPersonDetailsFromDirectory(row, employeeDirectoryMap, 'emp_no'))
        .filter(Boolean)
        .filter((detail) => {
          const key = String(detail.emp_id || detail.emp_no || detail.name || '').toLowerCase();
          return key && !seenKeys.has(key);
        })
        .map((detail) => ({ name: detail.name || '-', details: detail, isSenior: false }));

      const merged = [...rowsFromIds, ...extraFromDetails];
      if (merged.length > 0) return merged;

      const rowsFromDisplay = buildPeopleRows(
        projectSrArchitectsDisplay,
        projectSrArchitectsDetails,
        employeeDirectoryMap,
        'emp_no',
      );
      if (rowsFromDisplay.length > 0) {
        return rowsFromDisplay.map((row, index) => ({ ...row, isSenior: index === 0 }));
      }

      return buildPeopleRows(srArchitectRawValue, [], employeeDirectoryMap, 'emp_no')
        .map((row, index) => ({ ...row, isSenior: index === 0 }));
    })()
  );
  const managerRows = buildManagerRows(projectRecord, baseData);
  const deliveryManagerRef = firstNonNullish(
    toDisplayTextOrNull(projectRecord?.delivery_manager_id),
    toDisplayTextOrNull(getNestedValue(baseData, 'data.delivery_manager_id')),
    null,
  );
  const deliveryManagerDetails = enrichPersonDetailsFromDirectory(firstNonNullish(
    projectRecord?.delivery_manager_details,
    getNestedValue(baseData, 'data.delivery_manager_details'),
    getEmployeeFromDirectory(employeeDirectoryMap, deliveryManagerRef, 'emp_no'),
    getEmployeeFromDirectory(employeeDirectoryMap, deliveryManagerRef, 'emp_id'),
    null,
  ), employeeDirectoryMap, 'emp_no');
  const deliveryManagerDisplay = firstNonNullish(
    deliveryManagerDetails?.name,
    toDisplayTextOrNull(projectRecord?.delivery_manager_display),
    toDisplayTextOrNull(getNestedValue(baseData, 'data.delivery_manager_display')),
    null,
  );
  const deliveryManagerRows = deliveryManagerDisplay
    ? [{ name: deliveryManagerDisplay, details: deliveryManagerDetails, isSenior: false }]
    : [];

  return {
    ...baseData,
    projectHeader: { ...resolvedHeader },
    peopleData: {
      ...baseData?.peopleData,
      srManagerDisplay: srManagerDisplay || '-',
      srManagerDetails,
      managerRows,
      deliveryManagerRows,
      architects: architectRows,
      srArchitects: srArchitectRows,
      architectsCombined: mergePeopleRows(srArchitectRows, architectRows),
    },
  };
};

const firstNonBlankProjectValue = (records, resolver) => (
  records
    .map((record) => (record && typeof record === 'object' ? resolver(record) : ''))
    .find((value) => value !== null && value !== undefined && String(value).trim() !== '')
);

const mergeProjectRecords = (...records) => {
  const mergedRecord = records.reduce((acc, record) => (
    !record || typeof record !== 'object' ? acc : { ...acc, ...record }
  ), {});
  const preservedHealth = firstNonBlankProjectValue(records, (record) => (
    record?.health
    ?? record?.rag_status_color
    ?? record?.pmo_status_color
    ?? record?.project?.health
    ?? record?.project?.rag_status_color
    ?? ''
  ));
  const preservedOverviewAccentColor = firstNonBlankProjectValue(records, (record) => (
    record?.overviewAccentColor
    ?? record?.overview_accent_color
    ?? record?.project?.overviewAccentColor
    ?? record?.project?.overview_accent_color
    ?? record?.health
    ?? record?.rag_status_color
    ?? record?.pmo_status_color
    ?? record?.project?.health
    ?? record?.project?.rag_status_color
    ?? ''
  ));

  return normalizeProjectRecord({
    ...mergedRecord,
    ...(preservedHealth ? { health: preservedHealth } : {}),
    ...(preservedOverviewAccentColor ? { overviewAccentColor: preservedOverviewAccentColor } : {}),
  });
};

const buildEmptyBaseData = () => ({
  projectHeader: {
    title: '-',
    status: '-',
    statusTextColor: '',
    statusBgColor: '',
    ragStatusColor: '',
    client: '-',
    timeline: { rlsd: '-', rled: '-', acsd: '-', rped: '-', delay: null },
    basicDetails: [],
    resources: [],
    progress: [],
  },
  peopleData: {
    srManagerDisplay: '-',
    srManagerDetails: null,
    managerRows: [],
    deliveryManagerRows: [],
    architects: [],
    srArchitects: [],
    architectsCombined: [],
  },
  csatSummary: null,
  csatData: null,
  csatTableData: [],
  progressData: [],
  projectUnitsData: { isApplicable: false, rows: [], resourceCategoryOptions: [] },
  effortsData: { rows: [], totals: {}, total: 0 },
  auditSummary: null,
  auditScoreData: { process: { count: 0, average: 0, lastAudit: 0 }, quality: { count: 0, average: 0, lastAudit: 0 }, technology: { count: 0, average: 0, lastAudit: 0 }, history: [] },
  riskRegisterData: {},
  milestoneData: { selectedIndex: 0, summary: { total: 0, completed: 0, inProgress: 0, delayed: 0, planned: 0, completionPercent: 0 }, milestones: [] },
  resourceCountData: [],
  resourceAllocationData: { summary: { total: 0, open: 0, closed: 0, transferred: 0, allocated: 0, days: '0.00', hours: '0.00', overdue: 0 }, allocations: [] },
  revenueData: null,
  utilizationData: null,
  gmData: null,
});

const buildHeaderResourcesWithDateRows = ({ mappedData, baseData, projectRecord }) => {
  const { effortMetricsDisplay, resourcesPayload } = getHeaderMetricsSources(baseData);

  const effortLoggedOnDateValueRaw = firstNonNullish(
    toDisplayTextOrNull(effortMetricsDisplay?.effort_logged_latest_text),
    toDisplayTextOrNull(resourcesPayload?.latestmonthsloggedeffort),
    toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'resources.latestmonthsloggedeffort')),
    toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'data.resources.latestmonthsloggedeffort')),
    toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'latest_month_logged_effort_percent')),
    toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'latestmonthsloggedeffort')),
    toFiniteNumberOrNull(projectRecord?.effort_logging_in_darpan),
    toFiniteNumberOrNull(baseData?.effort_logging_in_darpan),
    toFiniteNumberOrNull(baseData?.effortLoggingInDarpan),
    toFiniteNumberOrNull(getDirectOrNestedValue(baseData, 'metrics.effort_logging_in_darpan')),
  );
  const effortFromDisplay = splitMetricDisplayText(effortLoggedOnDateValueRaw);
  const effortLoggedOnDateValue = (typeof effortLoggedOnDateValueRaw !== 'string' && effortLoggedOnDateValueRaw !== null)
    ? `${Number(effortLoggedOnDateValueRaw).toFixed(2)}%`
    : sanitizeMetricValueText(effortFromDisplay.value);
  const effortLoggedOnDateLabel = effortFromDisplay.date ? `Effort logged (On ${effortFromDisplay.date})` : 'Effort logged';

  const idleHoursOnDateValueRaw = firstNonNullish(
    toDisplayTextOrNull(effortMetricsDisplay?.idle_time_latest_text),
    toDisplayTextOrNull(resourcesPayload?.idleEffortsForLatestMonth),
    toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'resources.idleEffortsForLatestMonth')),
    toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'data.resources.idleEffortsForLatestMonth')),
    toDisplayTextOrNull(getDirectOrNestedValue(baseData, 'idleEffortsForLatestMonth')),
    toFiniteNumberOrNull(projectRecord?.idle_time_on_date),
    toFiniteNumberOrNull(projectRecord?.idle_time_hours_on_date),
    toFiniteNumberOrNull(projectRecord?.idle_time_in_hrs_on_date),
    toFiniteNumberOrNull(baseData?.idle_time_on_date),
    toFiniteNumberOrNull(baseData?.idle_time_hours_on_date),
    toFiniteNumberOrNull(baseData?.idle_time_in_hrs_on_date),
    toFiniteNumberOrNull(baseData?.idleTimeOnDate),
    toFiniteNumberOrNull(baseData?.idleTimeHoursOnDate),
    toFiniteNumberOrNull(getDirectOrNestedValue(baseData, 'metrics.idle_time_on_date')),
  );
  const idleFromDisplay = splitMetricDisplayText(idleHoursOnDateValueRaw);
  let idleHoursOnDateValue = '0.00';
  if (idleHoursOnDateValueRaw !== null) {
    idleHoursOnDateValue = typeof idleHoursOnDateValueRaw === 'string'
      ? sanitizeMetricValueText(idleFromDisplay.value, '0.00')
      : sanitizeMetricValueText(Number(idleHoursOnDateValueRaw).toFixed(2), '0.00');
  }
  const idleHoursOnDateLabel = idleFromDisplay.date ? `Idle time in hrs (On ${idleFromDisplay.date})` : 'Idle time in hrs';

  const resourcesBase = Array.isArray(mappedData?.projectHeader?.resources) ? mappedData.projectHeader.resources : [];
  const hasEffortOnDate = resourcesBase.some((item) => String(item?.label || '').startsWith('Effort logged (On '));
  const hasIdleOnDate = resourcesBase.some((item) => String(item?.label || '').startsWith('Idle time in hrs (On '));

  return [
    ...resourcesBase,
    ...(hasEffortOnDate ? [] : [{ icon: EffortIcon, label: effortLoggedOnDateLabel, value: effortLoggedOnDateValue }]),
    ...(hasIdleOnDate ? [] : [{ icon: IdleIcon, label: idleHoursOnDateLabel, value: idleHoursOnDateValue }]),
  ];
};

const resolveCsatData = (baseData) => {
  const rawCsatRows = getRawCsatRowsFromPayload(baseData);
  if (!rawCsatRows?.some(isDbCsatRow)) return { csatSummary: null, csatData: null, csatTableData: [] };
  const customerLookup = buildCsatCustomerLookup(getRawCsatCustomerRowsFromPayload(baseData));
  return buildCsatViewModelFromDbRows(rawCsatRows, customerLookup);
};

const resolveAuditData = (baseData) => {
  const rawAuditDateRows = getRawAuditDateRowsFromPayload(baseData);
  const rawAuditNameRows = getRawAuditNameRowsFromPayload(baseData);
  const rawAuditScoreRows = getRawAuditScoreRowsFromPayload(baseData);

  if (rawAuditDateRows?.some(isDbAuditDateRow) && rawAuditDateRows?.some(isAuditDatesNestedShape)) {
    return buildAuditViewModelFromAuditDates(rawAuditDateRows);
  }
  if (rawAuditDateRows?.some(isDbAuditDateRow) && rawAuditScoreRows?.some(isDbAuditScoreRow) && rawAuditNameRows?.some(isDbAuditNameRow)) {
    return buildAuditViewModelFromDbRows({ auditDates: rawAuditDateRows, auditNames: rawAuditNameRows, auditScores: rawAuditScoreRows });
  }
  return { auditSummary: null, auditScoreData: { process: { count: 0, average: 0, lastAudit: 0 }, quality: { count: 0, average: 0, lastAudit: 0 }, technology: { count: 0, average: 0, lastAudit: 0 }, history: [] } };
};

// ---------------------------------------------------------------------------
// Primary Path: Render Backend Groups Directly (No Fallback Combining)
// ---------------------------------------------------------------------------
const mapBackendGroupsToViewModel = (backendGroups = [], resourceTypeLookup = null, resourceCategoryLookup = null) => {
  if (!Array.isArray(backendGroups) || backendGroups.length === 0) return null;

  let allRows = [];
  let grandTotalHours = 0;

  // Render ONLY groups returned by the backend (Initial, CRs with efforts, Base Added)
  const groups = backendGroups.map((group, index) => {
    const childRows = (Array.isArray(group.items) ? group.items : [])
      .map((item, itemIdx) => buildLegacyEffortRow(item, itemIdx, resourceTypeLookup, resourceCategoryLookup))
      .sort(compareEffortEstimationRows);

    allRows = [...allRows, ...childRows];

    const groupTotal = Number(group.total_hours) || childRows.reduce((sum, row) => sum + Number(row.efforts_hours_display || row.efforts_hours || 0), 0);
    grandTotalHours += groupTotal;

    const isInitial = group.group_type === 'INITIAL' || group.cr_id === null;
    const isBaseAdded = group.group_type === 'BASE_ADDED' || group.cr_id === 0;

    return {
      id: group.group_id || `group_${index + 1}`,
      row_id: group.group_id || `group_${index + 1}`,
      displayOrder: isInitial ? 1 : (isBaseAdded ? 3 : 2),
      crNumber: group.cr_number || '-',
      crName: group.cr_name || (isInitial ? 'Initial Efforts Estimation' : '-'),
      effortInvestment: formatCrInvestmentValue(group.efforts_investment),
      crStatus: group.cr_status || '-',
      customerApprovalStatus: formatCrCustomerApprovalValue(group.customer_status),
      totalHours: groupTotal,
      children: childRows,
      isInitialGroup: isInitial,
      isBaseAddedGroup: isBaseAdded,
      _hiddenByLegacyCondition: false,
    };
  });

  const totals = EFFORT_ESTIMATION_TOTAL_FIELDS.reduce((acc, field) => {
    acc[field] = allRows.reduce((sum, row) => sum + toEffortEstimationNumber(row[field]), 0);
    return acc;
  }, {});

  return {
    rows: allRows,
    groups,
    totals,
    total: grandTotalHours,
  };
};

// Effort Data Resolver: Primary Path Enforcement--
const resolveEffortsData = (baseData) => {
  // 1. Resolve backend pre-grouped array from all possible payload locations
  const backendGroups = firstArray([
    baseData?.groups,
    baseData?.data?.groups,
    baseData?.effortsData?.groups,
    baseData?.effort_groups,
  ]);

  const {
    resourceTypeLookup,
    resourceTypeOptions,
    resourceCategoryOptions: fallbackResourceCategoryOptions,
  } = buildResourceTypeMeta(getRawResourceTypeRowsFromPayload(baseData));

  const rawResourceCategoryRows = getRawResourceCategoryRowsFromPayload(baseData);
  const resourceCategoryLookup = buildResourceCategoryLookup(rawResourceCategoryRows);
  const resourceCategoryOptions = buildResourceCategoryOptions(rawResourceCategoryRows);

  let effortsBase = null;

  // 2. PRIMARY PATH: Execute whenever 'groups' array exists (even if empty `[]`)
  if (Array.isArray(backendGroups)) {
    effortsBase = mapBackendGroupsToViewModel(backendGroups, resourceTypeLookup, resourceCategoryLookup);
  }

  // 3. FALLBACK PATH: Execute ONLY when 'groups' property is completely absent
  if (!effortsBase) {
    const rawInitialEffortRows = getRawInitialEffortRowsFromPayload(baseData);
    const rawChangeRequests = getRawChangeRequestsFromPayload(baseData);

    if (rawInitialEffortRows || rawChangeRequests) {
      effortsBase = buildLegacyEffortEstimationViewModel({
        initialEffortRows: rawInitialEffortRows,
        changeRequests: rawChangeRequests,
        resourceTypeLookup,
        resourceCategoryLookup,
      });
    } else {
      effortsBase = baseData?.effortsData || null;
    }
  }

  return effortsBase ? {
    ...effortsBase,
    resourceTypeOptions,
    resourceCategoryOptions: resourceCategoryOptions.length
      ? resourceCategoryOptions
      : fallbackResourceCategoryOptions,
  } : effortsBase;
};

const getRawTbhResourceAllocationsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return [];
  const dataObj = payload.data ?? payload;
  return dataObj.tbhResourceAllocations ?? dataObj.tbh_resource_allocations ?? [];
};

const buildTbhResourceAllocationsVm = (rows) => {
  const items = Array.isArray(rows) ? rows : [];
  return items.map((row) => ({
    id: row.id,
    tbhId: row.tbh_id || '',
    projectId: row.project_id || '',
    projectName: '',
    projectStatus: '',
    srNumber: '',
    vertical: row.vertical || '',
    regionName: row.region_name || '',
    countryName: row.country_name || '',
    roleName: row.role_name || '',
    experience: row.experience || '',
    location: row.location || '',
    locationId: row.location_id ?? row.locationId,
    department: row.department || '',
    allocationPct: Number(row.allocation_pct || 0).toFixed(2),
    startDate: row.allocation_start_date || '',
    endDate: row.allocation_end_date || '',
    allocatedDays: Number(firstNonNullish(
      toFiniteNumberOrNull(row?.allocated_days),
      toFiniteNumberOrNull(row?.allocatedDays),
    ) || 0).toFixed(2),
    allocatedHours: Number(firstNonNullish(
      toFiniteNumberOrNull(row?.allocated_hours),
      toFiniteNumberOrNull(row?.allocatedHours),
    ) || 0).toFixed(2),
    remark: row.remark || '',
    scrumTeamName: row.scrum_team_name || '',
    isActive: row.is_active,
    rawRow: row,
    requestId: row.requestId ?? row.request_id,
    regionId: row.regionId ?? row.region_id,
    countryId: row.countryId ?? row.country_id,
    verticalId: row.verticalId ?? row.vertical_id,
    roleId: row.roleId ?? row.role_id,
    experienceId: row.experienceId ?? row.experience_id,
    scrumTeamId: row.scrumTeamId ?? row.scrum_team_id,
    departmentId: row.departmentId ?? row.department_id,
  }));
};

const applyBackendDataToViewModel = (baseData, projectRecord, options = {}) => {
  const { skipSectionComputation = false } = options;
  const mappedData = applyProjectDetailsToViewModel(baseData, projectRecord);
  const revenueResolved = resolveRevenueData(baseData);
  const gmData = resolveGmData(baseData, projectRecord);
  const csatResolved = resolveCsatData(baseData);
  const { progressHydrated, progressSummaryLabel, progressSummaryValue, progressSummarySortValue, jiraSummaryValue } = buildProgressSummary(baseData);
  const resourcesWithDateRows = buildHeaderResourcesWithDateRows({ mappedData, baseData, projectRecord });
  const auditResolved = resolveAuditData(baseData);
  const effortsResolved = resolveEffortsData(baseData);
  const projectUnitsData = buildProjectUnitsData(baseData, mappedData?.projectHeader?.timeline?.projectType);
  const employeeDirectoryMap = getEmployeeDirectoryMap(baseData);
  const { resourceTypeLookup, resourceTypeOrderLookup, resourceTypeOptions } = buildResourceTypeMeta(getRawResourceTypeRowsFromPayload(baseData));
  const utilizationData = buildUtilizationData(baseData);

  let riskRegisterData = {};
  let resourcesResolved = null;
  let tbhResources = [];

  if (!skipSectionComputation) {
    riskRegisterData = buildRiskRegisterData(baseData);
    const rawResourceAllocRows = getRawResourceAllocRowsFromPayload(baseData);
    const rawTbhAllocRows = getRawTbhResourceAllocationsFromPayload(baseData);
    resourcesResolved = rawResourceAllocRows
      ? buildResourceAllocVm(rawResourceAllocRows, {
        employeeDirectoryMap,
        resourceTypeLookup,
        resourceTypeOrderLookup,
        tbhRows: rawTbhAllocRows,
      })
      : null;
    tbhResources = buildTbhResourceAllocationsVm(rawTbhAllocRows);
  }

  return {
    ...mappedData,
    ...revenueResolved,
    gmData,
    utilizationData: utilizationData || mappedData.utilizationData,
    riskRegisterData,
    ...csatResolved,
    progressData: progressHydrated,
    projectHeader: {
      ...mappedData.projectHeader,
      resources: resourcesWithDateRows,
      progress: [
        { icon: EffortIcon, label: progressSummaryLabel, value: progressSummaryValue, sortValue: progressSummarySortValue },
        { icon: JiraIcon, label: 'Jira progress %', value: jiraSummaryValue },
      ],
    },
    ...(skipSectionComputation
      ? { auditSummary: auditResolved.auditSummary, auditScoreData: { process: { count: 0, average: 0, lastAudit: 0 }, quality: { count: 0, average: 0, lastAudit: 0 }, technology: { count: 0, average: 0, lastAudit: 0 }, history: [] } }
      : auditResolved),
    effortsData: effortsResolved || mappedData.effortsData,
    projectUnitsData,
    resourceTypeOptions,
    employeeDirectory: baseData?.employeeDirectory || [],
    ...(resourcesResolved ? { resourceAllocationData: resourcesResolved.resourceAllocationData, resourceCountData: resourcesResolved.resourceCountData } : null),
    peopleData: mappedData.peopleData,
    tbhResources,
  };
};

// ---------------------------------------------------------------------------
// Lazy section data builders
// ---------------------------------------------------------------------------

const computeAuditScoreData = (baseData) => resolveAuditData(baseData);

const computeRiskRegisterData = (baseData) => buildRiskRegisterData(baseData);

const computeResourceData = (baseData) => {
  const employeeDirectoryMap = getEmployeeDirectoryMap(baseData);
  const { resourceTypeLookup, resourceTypeOrderLookup } = buildResourceTypeMeta(getRawResourceTypeRowsFromPayload(baseData));
  const rawResourceAllocRows = getRawResourceAllocRowsFromPayload(baseData);
  const rawTbhAllocRows = getRawTbhResourceAllocationsFromPayload(baseData);
  if (!rawResourceAllocRows) return null;
  return buildResourceAllocVm(rawResourceAllocRows, {
    employeeDirectoryMap,
    resourceTypeLookup,
    resourceTypeOrderLookup,
    tbhRows: rawTbhAllocRows,
  });
};

const computeTbhResources = (baseData) => {
  const rawTbhAllocRows = getRawTbhResourceAllocationsFromPayload(baseData);
  return buildTbhResourceAllocationsVm(rawTbhAllocRows);
};

export {
  applyBackendDataToViewModel,
  buildEmptyBaseData,
  getResponseData,
  mergeProjectRecords,
  toFiniteNumberOrNull,
  computeAuditScoreData,
  computeRiskRegisterData,
  computeResourceData,
  computeTbhResources,
};
